import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Routes, Route, NavLink, Navigate } from "react-router-dom";
import {
  MasterCrudPage,
  useRouteModulePermissions,
  DataTable,
  PageTitle,
} from "@/components/common/Crud";
import { useAuth } from "@/context/AuthContext";
import api, { BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { DatePicker } from "@/components/common/DatePicker";
import ModernLoader from "@/components/common/ModernLoader";
import {
  Plus,
  Pencil,
  Trash2,
  Search,
  Download,
  X,
  ChevronDown,
  Printer,
  Eye,
  CheckCircle2,
} from "lucide-react";
import { downloadCSV } from "@/lib/csv";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
} from "recharts";

// ─── Sub-nav ─────────────────────────────────────────────────────────────────

function Sub({ items, prefix }) {
  return (
    <div className="border-b border-slate-200 mb-3 flex flex-wrap gap-1 text-xs">
      {items.map((i) => (
        <NavLink
          key={i.to}
          to={i.to}
          end={i.end}
          data-testid={`${prefix}-${i.to}`}
          className={({ isActive }) =>
            `px-3 py-1.5 border-b-2 -mb-px ${
              isActive
                ? "border-blue-600 text-blue-600 font-medium"
                : "border-transparent text-slate-600 hover:text-slate-900"
            }`
          }
        >
          {i.label}
        </NavLink>
      ))}
    </div>
  );
}

// ─── Lesson Plan context ──────────────────────────────────────────────────────

const LpCtx = createContext(null);
const useLp = () => useContext(LpCtx);

// ─── Form field helpers ───────────────────────────────────────────────────────

function FLabel({ children, required }) {
  return (
    <Label className="text-xs font-semibold text-slate-700 mb-1 block">
      {children}
      {required && <span className="text-red-500 ml-0.5">*</span>}
    </Label>
  );
}

function FSel({
  label,
  value,
  onChange,
  options,
  required,
  placeholder = "— Select —",
}) {
  return (
    <div>
      <FLabel required={required}>{label}</FLabel>
      <select
        className="h-9 w-full rounded-md border border-slate-300 text-sm px-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">{placeholder}</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function FTxt({
  label,
  value,
  onChange,
  required,
  type = "text",
  placeholder,
  full,
}) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <FLabel required={required}>{label}</FLabel>
      <Input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="h-9"
      />
    </div>
  );
}

function FDate({ label, value, onChange }) {
  return (
    <div>
      <FLabel>{label}</FLabel>
      <DatePicker value={value ?? ""} onChange={onChange} className="h-9" />
    </div>
  );
}

function FTxa({ label, value, onChange, rows = 3 }) {
  return (
    <div className="md:col-span-2">
      <FLabel>{label}</FLabel>
      <textarea
        rows={rows}
        className="w-full rounded-md border border-slate-300 text-sm px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

function stripHtml(value) {
  return String(value || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function periodToLetter(periodNo) {
  const n = Number(periodNo || 0);
  if (!Number.isFinite(n) || n <= 0) return "";
  if (n <= 26) return String.fromCharCode(64 + n);
  return `P${n}`;
}

function dayNameFromDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(`${dateStr}T00:00:00`);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", { weekday: "long" });
}

function RichEditor({ label, value, onChange, required }) {
  const [html, setHtml] = useState(value || "");

  useEffect(() => {
    setHtml(value || "");
  }, [value]);

  const exec = (cmd, arg = null) => {
    document.execCommand(cmd, false, arg);
  };

  const insertLink = () => {
    const url = window.prompt("Enter link URL");
    if (!url) return;
    exec("createLink", url);
  };

  const insertImage = () => {
    const url = window.prompt("Enter image URL");
    if (!url) return;
    exec("insertImage", url);
  };

  return (
    <div className="md:col-span-2">
      <FLabel required={required}>{label}</FLabel>
      <div className="border border-slate-300 rounded-md overflow-hidden bg-white">
        <div className="flex flex-wrap gap-1 p-1.5 border-b border-slate-200 bg-slate-50">
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-7 px-2 text-xs"
            onClick={() => exec("bold")}
          >
            B
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-7 px-2 text-xs italic"
            onClick={() => exec("italic")}
          >
            I
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-7 px-2 text-xs underline"
            onClick={() => exec("underline")}
          >
            U
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-7 px-2 text-xs"
            onClick={() => exec("insertUnorderedList")}
          >
            List
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-7 px-2 text-xs"
            onClick={insertLink}
          >
            Link
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-7 px-2 text-xs"
            onClick={insertImage}
          >
            Image
          </Button>
        </div>
        <div
          className="min-h-[110px] p-2 text-sm focus:outline-none"
          contentEditable
          suppressContentEditableWarning
          dangerouslySetInnerHTML={{ __html: html }}
          onInput={(e) => {
            const next = e.currentTarget.innerHTML;
            setHtml(next);
            onChange(next);
          }}
        />
      </div>
    </div>
  );
}

// ─── Shared CRUD hook ─────────────────────────────────────────────────────────

function useLpCrud(endpoint) {
  const { branchId, sessionId } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleteRow, setDeleteRow] = useState(null);
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(endpoint, {
        params: { branch_id: branchId, session_id: sessionId, limit: 500 },
      });
      setRows(data.items || []);
    } catch {
      toast.error("Failed to load");
    }
    setLoading(false);
  }, [endpoint, branchId, sessionId]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = useCallback((init = {}) => {
    setEditing(null);
    setForm(init);
    setOpen(true);
  }, []);

  const openEdit = useCallback((row) => {
    setEditing(row);
    setForm({ ...row });
    setOpen(true);
  }, []);

  const save = useCallback(async () => {
    if (saving) return false;
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.branch_id && branchId) payload.branch_id = Number(branchId);
      if (!payload.session_id && sessionId)
        payload.session_id = Number(sessionId);
      if (editing) {
        await api.put(`${endpoint}/${editing.id}`, payload);
        toast.success("Updated successfully");
      } else {
        await api.post(endpoint, payload);
        toast.success("Created successfully");
      }
      setOpen(false);
      load();
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
      return false;
    } finally {
      setSaving(false);
    }
  }, [saving, form, branchId, sessionId, editing, endpoint, load]);

  const del = useCallback(async () => {
    if (!deleteRow?.id) return;
    try {
      await api.delete(`${endpoint}/${deleteRow.id}`);
      toast.success("Deleted");
      setDeleteRow(null);
      load();
    } catch {
      toast.error("Delete failed");
    }
  }, [deleteRow, endpoint, load]);

  return {
    rows,
    loading,
    open,
    setOpen,
    editing,
    form,
    setForm,
    saving,
    deleteRow,
    setDeleteRow,
    search,
    setSearch,
    openCreate,
    openEdit,
    save,
    del,
    load,
  };
}

// ─── List table ───────────────────────────────────────────────────────────────

function LpTable({
  title,
  columns,
  rows,
  loading,
  search,
  setSearch,
  onAdd,
  onEdit,
  onDelete,
  onPrint,
  testid,
}) {
  const exportCSV = () => {
    if (!rows.length) return toast.info("Nothing to export");
    downloadCSV(
      rows,
      columns,
      `${title.toLowerCase().replace(/\s+/g, "_")}.csv`,
    );
  };

  return (
    <div data-testid={testid}>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <h1 className="text-xl font-bold tracking-tight font-serif-display erp-text-primary">
          {title}
        </h1>
        <div className="flex gap-2 flex-wrap">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search..."
              className="pl-9 h-9 w-44 md:w-56"
            />
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={exportCSV}
            className="h-9"
          >
            <Download className="w-3.5 h-3.5 mr-1" />
            Excel
          </Button>
          {onAdd && (
            <Button size="sm" onClick={onAdd} className="h-9 erp-primary">
              <Plus className="w-3.5 h-3.5 mr-1" />
              Add New
            </Button>
          )}
        </div>
      </div>

      {loading ? (
        <ModernLoader
          compact
          title={`Loading ${title}`}
          subtitle="Fetching records"
        />
      ) : (
        <div
          className="border rounded-md overflow-hidden bg-white shadow-sm"
          style={{ borderColor: "var(--erp-border)" }}
        >
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse min-w-[640px]">
              <thead className="bg-slate-50 text-slate-700 text-xs uppercase tracking-wide">
                <tr>
                  {columns.map((c) => (
                    <th
                      key={c.key}
                      className="px-3 py-2 font-semibold border-b border-slate-200"
                    >
                      {c.label}
                    </th>
                  ))}
                  {(onEdit || onDelete || onPrint) && (
                    <th className="px-3 py-2 font-semibold border-b border-slate-200 w-24 text-right">
                      Actions
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="text-sm">
                {rows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={columns.length + 1}
                      className="px-3 py-12 text-center text-slate-400 text-xs"
                    >
                      <div className="font-medium mb-1">No records yet</div>
                      <div className="text-[11px] text-slate-300">
                        Click "Add New" to create the first one
                      </div>
                    </td>
                  </tr>
                ) : (
                  rows.map((row, i) => (
                    <tr
                      key={row.id || i}
                      className="border-b border-slate-100 hover:bg-slate-50/60 transition-colors"
                    >
                      {columns.map((c) => (
                        <td
                          key={c.key}
                          className="px-3 py-2 align-middle text-slate-800"
                        >
                          {c.render
                            ? c.render(row)
                            : (row[c.key] ?? (
                                <span className="text-slate-300">—</span>
                              ))}
                        </td>
                      ))}
                      {(onEdit || onDelete || onPrint) && (
                        <td className="px-3 py-2 text-right">
                          <div className="flex justify-end gap-1">
                            {onPrint && (
                              <button
                                className="p-1.5 rounded hover:bg-slate-100 text-slate-600"
                                onClick={() => onPrint(row)}
                                title="Print A4"
                              >
                                <Printer className="w-3.5 h-3.5" />
                              </button>
                            )}
                            {onEdit && (
                              <button
                                className="p-1.5 rounded hover:bg-blue-50 erp-text-primary"
                                onClick={() => onEdit(row)}
                              >
                                <Pencil className="w-3.5 h-3.5" />
                              </button>
                            )}
                            {onDelete && (
                              <button
                                className="p-1.5 rounded hover:bg-red-50 text-red-600"
                                onClick={() => onDelete(row)}
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Delete confirm dialog ────────────────────────────────────────────────────

function DelConfirm({ row, onConfirm, onCancel }) {
  return (
    <AlertDialog open={!!row} onOpenChange={(v) => !v && onCancel()}>
      <AlertDialogContent className="max-w-md border-slate-200">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-xl text-slate-900">
            Delete {row?.name || row?.title || `#${row?.id}`}?
          </AlertDialogTitle>
          <AlertDialogDescription className="text-sm leading-6 text-slate-600">
            This action cannot be undone and the selected record will be
            permanently removed.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            className="bg-rose-600 hover:bg-rose-700 text-white"
          >
            Delete
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// ─── Terms Tab ────────────────────────────────────────────────────────────────

function TermsTab() {
  const { reloadTerms } = useLp();
  const crud = useLpCrud("/terms");
  const permissions = useRouteModulePermissions("lesson_plan");

  const filtered = useMemo(() => {
    const q = crud.search.trim().toLowerCase();
    if (!q) return crud.rows;
    return crud.rows.filter((r) =>
      String(r.name || "")
        .toLowerCase()
        .includes(q),
    );
  }, [crud.rows, crud.search]);

  const handleSave = async () => {
    if (!crud.form.name?.trim()) return toast.error("Term name is required");
    const ok = await crud.save();
    if (ok) reloadTerms();
  };

  const handleDel = async () => {
    await crud.del();
    reloadTerms();
  };

  return (
    <>
      <LpTable
        title="Terms / Examinations"
        testid="lp-terms"
        search={crud.search}
        setSearch={crud.setSearch}
        loading={crud.loading}
        rows={filtered}
        onAdd={permissions.canAdd ? () => crud.openCreate() : null}
        onEdit={permissions.canEdit ? crud.openEdit : null}
        onDelete={permissions.canDelete ? crud.setDeleteRow : null}
        columns={[
          { key: "name", label: "Term Name" },
          { key: "start_date", label: "Start Date" },
          { key: "end_date", label: "End Date" },
          { key: "description", label: "Description" },
        ]}
      />

      <Dialog open={crud.open} onOpenChange={crud.setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif-display erp-text-primary">
              {crud.editing ? "Edit" : "Add New"} · Term / Examination
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <FTxt
              label="Term Name"
              required
              full
              value={crud.form.name}
              onChange={(v) => crud.setForm((f) => ({ ...f, name: v }))}
              placeholder="e.g. Term 1, Half Yearly, Annual"
            />
            <FDate
              label="Start Date"
              value={crud.form.start_date}
              onChange={(v) => crud.setForm((f) => ({ ...f, start_date: v }))}
            />
            <FDate
              label="End Date"
              value={crud.form.end_date}
              onChange={(v) => crud.setForm((f) => ({ ...f, end_date: v }))}
            />
            <FTxa
              label="Description"
              value={crud.form.description}
              onChange={(v) => crud.setForm((f) => ({ ...f, description: v }))}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => crud.setOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={crud.saving}
              className="erp-primary"
            >
              {crud.saving ? "Saving..." : crud.editing ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DelConfirm
        row={crud.deleteRow}
        onConfirm={handleDel}
        onCancel={() => crud.setDeleteRow(null)}
      />
    </>
  );
}

// ─── Chapters Tab ─────────────────────────────────────────────────────────────

function ChaptersTab() {
  const {
    classes,
    subjects,
    terms,
    allSections,
    reloadChapters,
    subjectGroups,
  } = useLp();
  const { branchId, sessionId } = useAuth();
  const crud = useLpCrud("/chapters");
  const permissions = useRouteModulePermissions("lesson_plan");

  // ── form state ───────────────────────────────────────────────────────────────
  const [formSections, setFormSections] = useState([]);
  const [selectedSectionIds, setSelectedSectionIds] = useState([]);
  const [chapterEntries, setChapterEntries] = useState([
    { name: "", order: "" },
  ]);
  const [editEntriesSeed, setEditEntriesSeed] = useState(null);
  const [bulkSaving, setBulkSaving] = useState(false);

  // ── filter bar state ─────────────────────────────────────────────────────────
  const [fClassId, setFClassId] = useState("");
  const [fSectionId, setFSectionId] = useState("");
  const [fSgId, setFSgId] = useState("");
  const [fSubjectId, setFSubjectId] = useState("");
  const [fTermId, setFTermId] = useState("");
  const [filterSections, setFilterSections] = useState([]);
  const [collapsedGroups, setCollapsedGroups] = useState(new Set());

  const resolve = (arr, id, field = "name") =>
    arr.find((x) => String(x.id) === String(id))?.[field] ?? null;

  // ── section loaders ──────────────────────────────────────────────────────────
  const loadSections = useCallback(
    async (classId) => {
      if (!classId) {
        setFormSections([]);
        return;
      }
      try {
        const { data } = await api.get("/sections", {
          params: { class_id: classId, branch_id: branchId, limit: 500 },
        });
        setFormSections(data.items || []);
      } catch {
        setFormSections([]);
      }
    },
    [branchId],
  );

  const loadFilterSections = useCallback(
    async (classId) => {
      if (!classId) {
        setFilterSections([]);
        setFSectionId("");
        return;
      }
      try {
        const { data } = await api.get("/sections", {
          params: { class_id: classId, branch_id: branchId, limit: 500 },
        });
        setFilterSections(data.items || []);
      } catch {
        setFilterSections([]);
      }
    },
    [branchId],
  );

  // ── dialog effects ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (crud.open && crud.form.class_id) loadSections(crud.form.class_id);
    if (!crud.open) {
      setFormSections([]);
      setSelectedSectionIds([]);
      setChapterEntries([{ name: "", order: "" }]);
      setEditEntriesSeed(null);
    }
  }, [crud.open, crud.form.class_id, loadSections]);

  useEffect(() => {
    if (!crud.open) return;
    setSelectedSectionIds(
      crud.editing && crud.form.section_id
        ? [String(crud.form.section_id)]
        : [],
    );
    setChapterEntries(
      crud.editing
        ? editEntriesSeed?.length
          ? editEntriesSeed
          : [
              {
                id: crud.form.id,
                name: String(crud.form.name || ""),
                order:
                  crud.form.order === 0 ? "0" : String(crud.form.order || ""),
              },
            ]
        : [{ name: "", order: "" }],
    );
  }, [
    crud.open,
    crud.editing,
    crud.form.section_id,
    crud.form.id,
    crud.form.name,
    crud.form.order,
    editEntriesSeed,
  ]);

  // ── row helpers ──────────────────────────────────────────────────────────────
  const addChapterRow = useCallback(
    () => setChapterEntries((p) => [...p, { name: "", order: "" }]),
    [],
  );
  const removeChapterRow = useCallback(
    (i) => setChapterEntries((p) => p.filter((_, j) => j !== i)),
    [],
  );
  const updateChapterRow = useCallback(
    (i, f, v) =>
      setChapterEntries((p) =>
        p.map((r, j) => (j === i ? { ...r, [f]: v } : r)),
      ),
    [],
  );

  // ── subject groups filtered by selected class (+ section name matching) ────────
  const sgsByFormClass = useMemo(() => {
    if (!crud.form.class_id) return subjectGroups;
    const sgs = subjectGroups.filter(
      (sg) => String(sg.class_id) === String(crud.form.class_id),
    );
    if (!selectedSectionIds.length) return sgs;
    // sections in SG are stored as names — resolve the selected section name
    const secName = allSections.find(
      (s) => String(s.id) === selectedSectionIds[0],
    )?.name;
    if (!secName) return sgs;
    return sgs.filter((sg) => (sg.sections || []).includes(secName));
  }, [subjectGroups, crud.form.class_id, selectedSectionIds, allSections]);

  const sgsByFilterClass = useMemo(() => {
    if (!fClassId) return subjectGroups;
    const sgs = subjectGroups.filter(
      (sg) => String(sg.class_id) === String(fClassId),
    );
    if (!fSectionId) return sgs;
    const secName = allSections.find(
      (s) => String(s.id) === String(fSectionId),
    )?.name;
    if (!secName) return sgs;
    return sgs.filter((sg) => (sg.sections || []).includes(secName));
  }, [subjectGroups, fClassId, fSectionId, allSections]);

  // ── subjects filtered by selected SG's subject list ──────────────────────────
  const subjectsByFormGroup = useMemo(() => {
    const sgId = crud.form.subject_group_id;
    if (!sgId) return subjects;
    const sg = subjectGroups.find((g) => String(g.id) === String(sgId));
    if (!sg || !Array.isArray(sg.subjects) || !sg.subjects.length)
      return subjects;
    const ids = new Set(sg.subjects.map(String));
    return subjects.filter((s) => ids.has(String(s.id)));
  }, [subjects, subjectGroups, crud.form.subject_group_id]);

  const subjectsByFilterGroup = useMemo(() => {
    if (!fSgId) return subjects;
    const sg = subjectGroups.find((g) => String(g.id) === String(fSgId));
    if (!sg || !Array.isArray(sg.subjects) || !sg.subjects.length)
      return subjects;
    const ids = new Set(sg.subjects.map(String));
    return subjects.filter((s) => ids.has(String(s.id)));
  }, [subjects, subjectGroups, fSgId]);

  // ── filtered + grouped rows ──────────────────────────────────────────────────
  const filteredRows = useMemo(() => {
    let rows = crud.rows;
    if (crud.search.trim()) {
      const q = crud.search.trim().toLowerCase();
      rows = rows.filter((r) =>
        [
          r.name,
          resolve(classes, r.class_id),
          resolve(subjects, r.subject_id),
          resolve(terms, r.term_id),
        ]
          .join(" ")
          .toLowerCase()
          .includes(q),
      );
    }
    if (fClassId) rows = rows.filter((r) => String(r.class_id) === fClassId);
    if (fSectionId)
      rows = rows.filter((r) => String(r.section_id) === fSectionId);
    if (fSubjectId)
      rows = rows.filter((r) => String(r.subject_id) === fSubjectId);
    if (fTermId) rows = rows.filter((r) => String(r.term_id) === fTermId);
    return rows;
  }, [
    crud.rows,
    crud.search,
    fClassId,
    fSectionId,
    fSubjectId,
    fTermId,
    classes,
    subjects,
    terms,
  ]);

  const groupedRows = useMemo(() => {
    const map = {};
    filteredRows.forEach((row) => {
      const key = `${row.term_id || ""}|${row.class_id || ""}|${row.section_id || ""}|${row.subject_id || ""}`;
      if (!map[key])
        map[key] = {
          key,
          termId: row.term_id,
          classId: row.class_id,
          sectionId: row.section_id,
          subjectId: row.subject_id,
          chapters: [],
        };
      map[key].chapters.push(row);
    });
    return Object.values(map).map((g) => ({
      ...g,
      chapters: [...g.chapters].sort(
        (a, b) => (a.order ?? 9999) - (b.order ?? 9999),
      ),
    }));
  }, [filteredRows]);

  const toggleGroup = useCallback((key) => {
    setCollapsedGroups((prev) => {
      const n = new Set(prev);
      n.has(key) ? n.delete(key) : n.add(key);
      return n;
    });
  }, []);

  const hasFilters = fClassId || fSectionId || fSgId || fSubjectId || fTermId;
  const clearFilters = useCallback(() => {
    setFClassId("");
    setFSectionId("");
    setFSgId("");
    setFSubjectId("");
    setFTermId("");
    setFilterSections([]);
  }, []);

  // ── duplicate / bulk helpers ─────────────────────────────────────────────────
  const duplicateKey = useCallback(
    (name, classId, sectionId, subjectId, termId) =>
      [
        String(name || "")
          .trim()
          .toLowerCase(),
        String(classId || ""),
        String(sectionId || ""),
        String(subjectId || ""),
        String(termId || ""),
      ].join("|"),
    [],
  );
  const existingKeys = useMemo(
    () =>
      new Set(
        crud.rows.map((r) =>
          duplicateKey(
            r.name,
            r.class_id,
            r.section_id,
            r.subject_id,
            r.term_id,
          ),
        ),
      ),
    [crud.rows, duplicateKey],
  );
  const createSectionIds = useMemo(
    () => (selectedSectionIds.length ? selectedSectionIds : [null]),
    [selectedSectionIds],
  );
  const validEntries = useMemo(
    () =>
      chapterEntries
        .map((r) => ({
          name: r.name.trim(),
          order:
            r.order !== "" && !Number.isNaN(Number(r.order))
              ? Number(r.order)
              : undefined,
        }))
        .filter((r) => r.name !== ""),
    [chapterEntries],
  );
  const previewCount = crud.editing
    ? 1
    : validEntries.length * createSectionIds.length;

  // ── save / delete ────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!crud.form.term_id) return toast.error("Term is required");
    if (!crud.form.class_id) return toast.error("Class is required");
    if (!crud.form.subject_id) return toast.error("Subject is required");
    if (
      formSections.length > 0 &&
      selectedSectionIds.length === 0 &&
      !crud.editing
    )
      return toast.error("Select at least one section");

    if (crud.editing) {
      const entries = chapterEntries
        .map((r) => ({
          id: r.id ? String(r.id) : null,
          name: String(r.name || "").trim(),
          order:
            r.order !== "" && !Number.isNaN(Number(r.order))
              ? Number(r.order)
              : undefined,
        }))
        .filter((r) => r.name !== "");
      if (!entries.length)
        return toast.error("Enter at least one chapter name");

      const targetSectionId =
        selectedSectionIds[0] ||
        (crud.form.section_id ? String(crud.form.section_id) : null);
      const editingIds = new Set(
        entries.filter((r) => r.id).map((r) => String(r.id)),
      );
      const localKeys = new Set(
        crud.rows
          .filter((r) => !editingIds.has(String(r.id)))
          .map((r) =>
            duplicateKey(
              r.name,
              r.class_id,
              r.section_id,
              r.subject_id,
              r.term_id,
            ),
          ),
      );

      setBulkSaving(true);
      let updated = 0;
      let created = 0;
      let skipped = 0;

      try {
        for (const row of entries) {
          const key = duplicateKey(
            row.name,
            crud.form.class_id,
            targetSectionId,
            crud.form.subject_id,
            crud.form.term_id,
          );
          if (localKeys.has(key)) {
            skipped++;
            continue;
          }

          if (row.id) {
            await api.put(`/chapters/${row.id}`, {
              ...crud.form,
              name: row.name,
              order: row.order,
              section_id: targetSectionId ? Number(targetSectionId) : undefined,
            });
            updated++;
          } else {
            await api.post("/chapters", {
              name: row.name,
              class_id: Number(crud.form.class_id),
              section_id: targetSectionId ? Number(targetSectionId) : undefined,
              subject_id: Number(crud.form.subject_id),
              term_id: Number(crud.form.term_id),
              order: row.order,
              branch_id: Number(branchId),
              session_id: sessionId ? Number(sessionId) : undefined,
            });
            created++;
          }

          localKeys.add(key);
        }

        toast.success(
          `Updated ${updated} chapter(s)` +
            (created ? `, created ${created} chapter(s)` : "") +
            (skipped ? `, skipped ${skipped} duplicate(s)` : ""),
        );
        crud.setOpen(false);
        crud.load();
        reloadChapters();
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Save failed");
      } finally {
        setBulkSaving(false);
      }
      return;
    }

    if (!validEntries.length)
      return toast.error("Enter at least one chapter name");
    const localKeys = new Set(existingKeys);
    let created = 0,
      skipped = 0;
    setBulkSaving(true);
    try {
      for (const sectionId of createSectionIds) {
        for (const row of validEntries) {
          const key = duplicateKey(
            row.name,
            crud.form.class_id,
            sectionId,
            crud.form.subject_id,
            crud.form.term_id,
          );
          if (localKeys.has(key)) {
            skipped++;
            continue;
          }
          await api.post("/chapters", {
            name: row.name,
            class_id: Number(crud.form.class_id),
            section_id: sectionId ? Number(sectionId) : undefined,
            subject_id: Number(crud.form.subject_id),
            term_id: Number(crud.form.term_id),
            order: row.order,
            branch_id: Number(branchId),
            session_id: sessionId ? Number(sessionId) : undefined,
          });
          localKeys.add(key);
          created++;
        }
      }
      if (created > 0) {
        toast.success(
          `Created ${created} chapter(s)` +
            (skipped ? `, skipped ${skipped} duplicate(s)` : ""),
        );
        crud.setOpen(false);
        crud.load();
        reloadChapters();
      } else {
        toast.error("No chapters created. All entries were duplicates.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    } finally {
      setBulkSaving(false);
    }
  };

  const handleDel = async () => {
    await crud.del();
    reloadChapters();
  };

  // infer subject_group_id from class + subject when opening edit
  const inferSgId = useCallback(
    (classId, subjectId) => {
      const sg = subjectGroups.find(
        (g) =>
          String(g.class_id) === String(classId) &&
          (g.subjects || []).some((sid) => String(sid) === String(subjectId)),
      );
      return sg ? String(sg.id) : "";
    },
    [subjectGroups],
  );

  const handleEdit = useCallback(
    (ch) => {
      const sameSubjectRows = crud.rows
        .filter(
          (r) =>
            String(r.term_id || "") === String(ch.term_id || "") &&
            String(r.class_id || "") === String(ch.class_id || "") &&
            String(r.section_id || "") === String(ch.section_id || "") &&
            String(r.subject_id || "") === String(ch.subject_id || ""),
        )
        .sort((a, b) => (a.order ?? 9999) - (b.order ?? 9999));

      setEditEntriesSeed(
        sameSubjectRows.map((r) => ({
          id: r.id,
          name: String(r.name || ""),
          order: r.order === 0 ? "0" : String(r.order || ""),
        })),
      );
      crud.openEdit({
        ...ch,
        subject_group_id: inferSgId(ch.class_id, ch.subject_id),
      });
    },
    [crud, inferSgId],
  );

  const exportCSV = () => {
    if (!filteredRows.length) return toast.info("Nothing to export");
    downloadCSV(
      filteredRows.map((r) => ({
        chapter: r.name,
        class: resolve(classes, r.class_id) || "",
        section: resolve(allSections, r.section_id) || "",
        subject: resolve(subjects, r.subject_id) || "",
        term: resolve(terms, r.term_id) || "",
        order: r.order ?? "",
      })),
      [
        { key: "chapter", label: "Chapter" },
        { key: "class", label: "Class" },
        { key: "section", label: "Section" },
        { key: "subject", label: "Subject" },
        { key: "term", label: "Term" },
        { key: "order", label: "Order" },
      ],
      "chapters.csv",
    );
  };

  const selCls =
    "h-8 text-xs rounded border border-slate-300 px-2 bg-white focus:outline-none focus:ring-1 focus:ring-blue-400";

  return (
    <>
      {/* ── header ── */}
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <h1 className="text-xl font-bold tracking-tight font-serif-display erp-text-primary">
          Chapters
        </h1>
        <div className="flex gap-2 flex-wrap">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <Input
              value={crud.search}
              onChange={(e) => crud.setSearch(e.target.value)}
              placeholder="Search..."
              className="pl-9 h-9 w-44 md:w-56"
            />
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={exportCSV}
            className="h-9"
          >
            <Download className="w-3.5 h-3.5 mr-1" />
            Excel
          </Button>
          {permissions.canAdd && (
            <Button
              size="sm"
              onClick={() => crud.openCreate()}
              className="h-9 erp-primary"
            >
              <Plus className="w-3.5 h-3.5 mr-1" />
              Add New
            </Button>
          )}
        </div>
      </div>

      {/* ── filter bar ── */}
      <div className="flex flex-wrap gap-2 mb-3 p-2.5 bg-slate-50 rounded-md border border-slate-200 items-center">
        <select
          value={fClassId}
          onChange={(e) => {
            setFClassId(e.target.value);
            loadFilterSections(e.target.value);
          }}
          className={selCls}
        >
          <option value="">All Classes</option>
          {classes.map((c) => (
            <option key={c.id} value={String(c.id)}>
              {c.name}
            </option>
          ))}
        </select>
        <select
          value={fSectionId}
          onChange={(e) => setFSectionId(e.target.value)}
          disabled={!fClassId}
          className={`${selCls} disabled:opacity-50`}
        >
          <option value="">All Sections</option>
          {filterSections.map((s) => (
            <option key={s.id} value={String(s.id)}>
              {s.name}
            </option>
          ))}
        </select>
        <select
          value={fSgId}
          onChange={(e) => {
            setFSgId(e.target.value);
            setFSubjectId("");
          }}
          className={selCls}
        >
          <option value="">All Subject Groups</option>
          {sgsByFilterClass.map((g) => (
            <option key={g.id} value={String(g.id)}>
              {g.name}
            </option>
          ))}
        </select>
        <select
          value={fSubjectId}
          onChange={(e) => setFSubjectId(e.target.value)}
          className={selCls}
        >
          <option value="">All Subjects</option>
          {subjectsByFilterGroup.map((s) => (
            <option key={s.id} value={String(s.id)}>
              {s.name}
            </option>
          ))}
        </select>
        <select
          value={fTermId}
          onChange={(e) => setFTermId(e.target.value)}
          className={selCls}
        >
          <option value="">All Terms</option>
          {terms.map((t) => (
            <option key={t.id} value={String(t.id)}>
              {t.name}
            </option>
          ))}
        </select>
        {hasFilters && (
          <button
            onClick={clearFilters}
            className="h-8 px-2 text-xs text-slate-500 hover:text-red-600 border border-slate-300 rounded bg-white"
          >
            ✕ Clear
          </button>
        )}
        <span className="text-xs text-slate-400 ml-auto">
          {filteredRows.length} chapter{filteredRows.length !== 1 ? "s" : ""}
        </span>
      </div>

      {/* ── grouped list ── */}
      {crud.loading ? (
        <ModernLoader
          compact
          title="Loading Chapters"
          subtitle="Fetching records"
        />
      ) : groupedRows.length === 0 ? (
        <div
          className="border rounded-md bg-white shadow-sm py-12 text-center text-slate-400 text-xs"
          style={{ borderColor: "var(--erp-border)" }}
        >
          <div className="font-medium mb-1">No chapters found</div>
          <div className="text-[11px] text-slate-300">
            Use "Add New" or adjust your filters
          </div>
        </div>
      ) : (
        <div
          className="border rounded-md overflow-hidden bg-white shadow-sm"
          style={{ borderColor: "var(--erp-border)" }}
        >
          {groupedRows.map((group) => {
            const isOpen = !collapsedGroups.has(group.key);
            const termName = resolve(terms, group.termId) || "—";
            const clsName = resolve(classes, group.classId) || "—";
            const secName = resolve(allSections, group.sectionId);
            const subName = resolve(subjects, group.subjectId) || "—";
            const sgName = inferSgId(group.classId, group.subjectId)
              ? subjectGroups.find(
                  (g) =>
                    String(g.id) === inferSgId(group.classId, group.subjectId),
                )?.name
              : null;
            return (
              <div key={group.key} className="border-b last:border-0">
                {/* group header */}
                <button
                  className="w-full flex items-center gap-2 px-4 py-2.5 bg-slate-50 hover:bg-slate-100 text-left transition-colors"
                  onClick={() => toggleGroup(group.key)}
                >
                  <ChevronDown
                    className={`w-3.5 h-3.5 text-slate-400 transition-transform flex-shrink-0 ${isOpen ? "" : "-rotate-90"}`}
                  />
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 flex-1 min-w-0 text-xs">
                    <span className="font-semibold erp-text-primary uppercase tracking-wide">
                      {termName}
                    </span>
                    <span className="text-slate-300">|</span>
                    <span className="font-medium text-slate-700">
                      {clsName}
                    </span>
                    {secName && (
                      <>
                        <span className="text-slate-300">|</span>
                        <span className="text-slate-600">Sec {secName}</span>
                      </>
                    )}
                    {sgName && (
                      <>
                        <span className="text-slate-300">|</span>
                        <span className="font-medium text-violet-600">
                          {sgName}
                        </span>
                      </>
                    )}
                    <span className="text-slate-300">|</span>
                    <span className="font-medium text-emerald-700">
                      {subName}
                    </span>
                  </div>
                  <span className="text-[11px] text-slate-400 flex-shrink-0">
                    {group.chapters.length} chapter
                    {group.chapters.length !== 1 ? "s" : ""}
                  </span>
                </button>

                {/* chapter rows */}
                {isOpen && (
                  <div>
                    {group.chapters.map((ch) => (
                      <div
                        key={ch.id}
                        className="flex items-center gap-3 px-4 py-2 pl-9 border-t border-slate-100 hover:bg-slate-50/70 transition-colors"
                      >
                        <span className="w-7 text-xs text-slate-400 font-mono text-right flex-shrink-0">
                          {ch.order ?? "—"}
                        </span>
                        <span className="flex-1 text-sm text-slate-800">
                          {ch.name}
                        </span>
                        <div className="flex gap-0.5 flex-shrink-0">
                          {permissions.canEdit && (
                            <button
                              className="p-1.5 rounded hover:bg-blue-50 erp-text-primary"
                              onClick={() => handleEdit(ch)}
                              title="Edit chapter"
                            >
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                          )}
                          {permissions.canDelete && (
                            <button
                              className="p-1.5 rounded hover:bg-red-50 text-red-600"
                              onClick={() => crud.setDeleteRow(ch)}
                              title="Delete chapter"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── dialog ── */}
      <Dialog open={crud.open} onOpenChange={crud.setOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif-display erp-text-primary">
              {crud.editing ? "Edit" : "Add New"} · Chapter
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <FSel
              label="Term"
              required
              value={crud.form.term_id}
              onChange={(v) => crud.setForm((f) => ({ ...f, term_id: v }))}
              options={terms.map((t) => ({
                value: String(t.id),
                label: t.name,
              }))}
            />
            <FSel
              label="Class"
              required
              value={crud.form.class_id}
              onChange={(v) => {
                crud.setForm((f) => ({ ...f, class_id: v, section_id: "" }));
                setSelectedSectionIds([]);
                loadSections(v);
              }}
              options={classes.map((c) => ({
                value: String(c.id),
                label: c.name,
              }))}
            />
            <FSel
              label="Subject Group"
              value={crud.form.subject_group_id}
              onChange={(v) =>
                crud.setForm((f) => ({
                  ...f,
                  subject_group_id: v,
                  subject_id: "",
                }))
              }
              options={sgsByFormClass.map((g) => ({
                value: String(g.id),
                label: g.name,
              }))}
              placeholder={
                crud.form.class_id
                  ? "— Select Subject Group —"
                  : "— Select Class first —"
              }
            />
            <FSel
              label="Subject"
              required
              value={crud.form.subject_id}
              onChange={(v) => crud.setForm((f) => ({ ...f, subject_id: v }))}
              options={subjectsByFormGroup.map((s) => ({
                value: String(s.id),
                label: s.name,
              }))}
            />

            {terms.length === 0 && (
              <div className="md:col-span-2 -mt-1">
                <p className="text-[11px] text-amber-600">
                  No terms found — go to the <strong>Terms</strong> tab and
                  create one first.
                </p>
              </div>
            )}

            <div className="md:col-span-2">
              <FLabel required={!crud.editing}>Section (multiple)</FLabel>
              {!crud.form.class_id ? (
                <p className="text-xs text-slate-500">Select class first</p>
              ) : formSections.length === 0 ? (
                <p className="text-xs text-slate-500">
                  No sections found for selected class
                </p>
              ) : (
                <div className="rounded-md border border-slate-300 p-2 max-h-36 overflow-auto">
                  <div className="mb-2 flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        setSelectedSectionIds(
                          formSections.map((s) => String(s.id)),
                        )
                      }
                    >
                      Select All
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setSelectedSectionIds([])}
                    >
                      Clear
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {formSections.map((s) => {
                      const sid = String(s.id);
                      return (
                        <label
                          key={sid}
                          className="flex items-center gap-2 text-sm"
                        >
                          <input
                            type="checkbox"
                            checked={selectedSectionIds.includes(sid)}
                            onChange={(e) => {
                              setSelectedSectionIds((prev) =>
                                e.target.checked
                                  ? [...prev, sid]
                                  : prev.filter((x) => x !== sid),
                              );
                              if (crud.editing)
                                crud.setForm((f) => ({
                                  ...f,
                                  section_id: e.target.checked ? sid : "",
                                }));
                            }}
                          />
                          <span>{s.name}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            <div className="md:col-span-2">
              <div className="flex items-center justify-between mb-2">
                <FLabel required>Chapter + Order (multiple)</FLabel>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={addChapterRow}
                  className="h-7 text-xs px-2"
                >
                  <Plus className="w-3 h-3 mr-1" />
                  Add More
                </Button>
              </div>
              <div className="space-y-2">
                {chapterEntries.map((entry, idx) => (
                  <div key={idx} className="flex gap-2 items-center">
                    <Input
                      placeholder="Chapter Name"
                      value={entry.name}
                      onChange={(e) =>
                        updateChapterRow(idx, "name", e.target.value)
                      }
                      className="h-9 flex-1"
                    />
                    <Input
                      type="number"
                      placeholder="Order"
                      value={entry.order}
                      onChange={(e) =>
                        updateChapterRow(idx, "order", e.target.value)
                      }
                      className="h-9 w-20"
                    />
                    {chapterEntries.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeChapterRow(idx)}
                        className="p-1.5 rounded hover:bg-red-50 text-red-500 flex-shrink-0"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-slate-500 mt-1.5">
                {crud.editing
                  ? "Edit opens all chapters for this subject context. Existing rows are updated together; additional rows create new chapters."
                  : "Duplicate chapters for same Class + Section + Subject + Term will be skipped."}
              </p>
            </div>

            {!crud.editing && previewCount > 0 && (
              <div className="md:col-span-2 rounded bg-blue-50 border border-blue-200 px-3 py-2 text-xs text-blue-800">
                Will try to create <strong>{previewCount}</strong> chapter
                entries ({validEntries.length} chapter(s) ×{" "}
                {createSectionIds.length} section(s)).
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => crud.setOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={crud.saving || bulkSaving}
              className="erp-primary"
            >
              {crud.saving || bulkSaving
                ? "Saving..."
                : !crud.editing && previewCount > 1
                  ? `Create ${previewCount} Chapters`
                  : crud.editing
                    ? "Update Chapters"
                    : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DelConfirm
        row={crud.deleteRow}
        onConfirm={handleDel}
        onCancel={() => crud.setDeleteRow(null)}
      />
    </>
  );
}

// ─── Topics Tab ───────────────────────────────────────────────────────────────

function TopicsTab() {
  const {
    classes,
    subjects,
    terms,
    allSections,
    chapters,
    reloadTopics,
    subjectGroups,
  } = useLp();
  const { branchId, sessionId } = useAuth();
  const crud = useLpCrud("/topics");
  const permissions = useRouteModulePermissions("lesson_plan");

  // ── dialog context selectors ─────────────────────────────────────────────────
  const [dClassId, setDClassId] = useState("");
  const [dSectionId, setDSectionId] = useState("");
  const [dSgId, setDSgId] = useState("");
  const [dSubjectId, setDSubjectId] = useState("");
  const [dSections, setDSections] = useState([]);

  // ── filter bar ───────────────────────────────────────────────────────────────
  const [fClassId, setFClassId] = useState("");
  const [fSectionId, setFSectionId] = useState("");
  const [fSgId, setFSgId] = useState("");
  const [fSubjectId, setFSubjectId] = useState("");
  const [fChapterId, setFChapterId] = useState("");
  const [filterSections, setFilterSections] = useState([]);
  const [collapsedGroups, setCollapsedGroups] = useState(new Set());

  // ── multi-topic rows ─────────────────────────────────────────────────────────
  const [topicEntries, setTopicEntries] = useState([{ name: "", order: "" }]);
  const [editTopicEntriesSeed, setEditTopicEntriesSeed] = useState(null);
  const [bulkSaving, setBulkSaving] = useState(false);

  const resolve = (arr, id, field = "name") =>
    arr.find((x) => String(x.id) === String(id))?.[field] ?? null;

  // ── section loaders ──────────────────────────────────────────────────────────
  const loadDialogSections = useCallback(
    async (classId) => {
      if (!classId) {
        setDSections([]);
        return;
      }
      try {
        const { data } = await api.get("/sections", {
          params: { class_id: classId, branch_id: branchId, limit: 500 },
        });
        setDSections(data.items || []);
      } catch {
        setDSections([]);
      }
    },
    [branchId],
  );

  const loadFilterSections = useCallback(
    async (classId) => {
      if (!classId) {
        setFilterSections([]);
        setFSectionId("");
        return;
      }
      try {
        const { data } = await api.get("/sections", {
          params: { class_id: classId, branch_id: branchId, limit: 500 },
        });
        setFilterSections(data.items || []);
      } catch {
        setFilterSections([]);
      }
    },
    [branchId],
  );

  // ── reset dialog state on open/close ────────────────────────────────────────
  useEffect(() => {
    if (!crud.open) {
      setDClassId("");
      setDSectionId("");
      setDSgId("");
      setDSubjectId("");
      setDSections([]);
      setTopicEntries([{ name: "", order: "" }]);
      setEditTopicEntriesSeed(null);
    } else if (!crud.editing) {
      // create mode — entries already reset above on close
      setTopicEntries([{ name: "", order: "" }]);
    } else {
      setTopicEntries(
        editTopicEntriesSeed?.length
          ? editTopicEntriesSeed
          : [
              {
                id: crud.form.id,
                name: String(crud.form.name || ""),
                order:
                  crud.form.order === 0 ? "0" : String(crud.form.order || ""),
              },
            ],
      );
    }
    // edit mode: state is set by handleTopicEdit before dialog opens
  }, [
    crud.open,
    crud.editing,
    crud.form.id,
    crud.form.name,
    crud.form.order,
    editTopicEntriesSeed,
  ]);

  // ── topic row helpers ────────────────────────────────────────────────────────
  const addTopicRow = useCallback(
    () => setTopicEntries((p) => [...p, { name: "", order: "" }]),
    [],
  );
  const removeTopicRow = useCallback(
    (i) => setTopicEntries((p) => p.filter((_, j) => j !== i)),
    [],
  );
  const updateTopicRow = useCallback(
    (i, f, v) =>
      setTopicEntries((p) => p.map((r, j) => (j === i ? { ...r, [f]: v } : r))),
    [],
  );

  // ── subject groups filtered by selected class + section ──────────────────────
  const sgsByDialogClass = useMemo(() => {
    if (!dClassId) return subjectGroups;
    const sgs = subjectGroups.filter(
      (sg) => String(sg.class_id) === String(dClassId),
    );
    if (!dSectionId) return sgs;
    const secName = allSections.find(
      (s) => String(s.id) === String(dSectionId),
    )?.name;
    if (!secName) return sgs;
    return sgs.filter((sg) => (sg.sections || []).includes(secName));
  }, [subjectGroups, dClassId, dSectionId, allSections]);

  const sgsByFilterClass = useMemo(() => {
    if (!fClassId) return subjectGroups;
    const sgs = subjectGroups.filter(
      (sg) => String(sg.class_id) === String(fClassId),
    );
    if (!fSectionId) return sgs;
    const secName = allSections.find(
      (s) => String(s.id) === String(fSectionId),
    )?.name;
    if (!secName) return sgs;
    return sgs.filter((sg) => (sg.sections || []).includes(secName));
  }, [subjectGroups, fClassId, fSectionId, allSections]);

  // ── subjects filtered by SG's subject list ───────────────────────────────────
  const subjectsByDialogGroup = useMemo(() => {
    if (!dSgId) return subjects;
    const sg = subjectGroups.find((g) => String(g.id) === String(dSgId));
    if (!sg || !Array.isArray(sg.subjects) || !sg.subjects.length)
      return subjects;
    const ids = new Set(sg.subjects.map(String));
    return subjects.filter((s) => ids.has(String(s.id)));
  }, [subjects, subjectGroups, dSgId]);

  const subjectsByFilterGroup = useMemo(() => {
    if (!fSgId) return subjects;
    const sg = subjectGroups.find((g) => String(g.id) === String(fSgId));
    if (!sg || !Array.isArray(sg.subjects) || !sg.subjects.length)
      return subjects;
    const ids = new Set(sg.subjects.map(String));
    return subjects.filter((s) => ids.has(String(s.id)));
  }, [subjects, subjectGroups, fSgId]);

  // ── chapters filtered for dialog (section-less chapters included when section set) ──
  const dialogChapters = useMemo(() => {
    return chapters.filter((ch) => {
      if (dClassId && String(ch.class_id) !== dClassId) return false;
      // include chapters with no section when user filters by section
      if (dSectionId && ch.section_id && String(ch.section_id) !== dSectionId)
        return false;
      if (dSubjectId && String(ch.subject_id) !== dSubjectId) return false;
      return true;
    });
  }, [chapters, dClassId, dSectionId, dSubjectId]);

  // ── filter bar chapters ──────────────────────────────────────────────────────
  const filterChapters = useMemo(() => {
    return chapters.filter((ch) => {
      if (fClassId && String(ch.class_id) !== fClassId) return false;
      if (fSectionId && ch.section_id && String(ch.section_id) !== fSectionId)
        return false;
      if (fSubjectId && String(ch.subject_id) !== fSubjectId) return false;
      return true;
    });
  }, [chapters, fClassId, fSectionId, fSubjectId]);

  // ── filtered + grouped rows ──────────────────────────────────────────────────
  const filteredRows = useMemo(() => {
    let rows = crud.rows;
    if (crud.search.trim()) {
      const q = crud.search.trim().toLowerCase();
      rows = rows.filter((r) => {
        const ch = chapters.find((c) => String(c.id) === String(r.chapter_id));
        return [r.name, ch?.name].join(" ").toLowerCase().includes(q);
      });
    }
    if (fChapterId)
      rows = rows.filter((r) => String(r.chapter_id) === fChapterId);
    else {
      const chapterIds = new Set(filterChapters.map((c) => String(c.id)));
      if (fClassId || fSectionId || fSubjectId)
        rows = rows.filter((r) => chapterIds.has(String(r.chapter_id)));
    }
    return rows;
  }, [
    crud.rows,
    crud.search,
    fChapterId,
    filterChapters,
    fClassId,
    fSectionId,
    fSubjectId,
    chapters,
  ]);

  const groupedRows = useMemo(() => {
    const map = {};
    filteredRows.forEach((row) => {
      const key = String(row.chapter_id || "none");
      if (!map[key]) map[key] = { key, chapterId: row.chapter_id, topics: [] };
      map[key].topics.push(row);
    });
    return Object.values(map).map((g) => ({
      ...g,
      topics: [...g.topics].sort(
        (a, b) => (a.order ?? 9999) - (b.order ?? 9999),
      ),
    }));
  }, [filteredRows]);

  const toggleGroup = useCallback((key) => {
    setCollapsedGroups((prev) => {
      const n = new Set(prev);
      n.has(key) ? n.delete(key) : n.add(key);
      return n;
    });
  }, []);

  const hasFilters =
    fClassId || fSectionId || fSgId || fSubjectId || fChapterId;
  const clearFilters = useCallback(() => {
    setFClassId("");
    setFSectionId("");
    setFSgId("");
    setFSubjectId("");
    setFChapterId("");
    setFilterSections([]);
  }, []);

  const validEntries = useMemo(
    () =>
      topicEntries
        .map((r) => ({
          name: r.name.trim(),
          order:
            r.order !== "" && !Number.isNaN(Number(r.order))
              ? Number(r.order)
              : undefined,
        }))
        .filter((r) => r.name !== ""),
    [topicEntries],
  );

  const duplicateTopicKey = useCallback(
    (name, chapterId) =>
      [
        String(name || "")
          .trim()
          .toLowerCase(),
        String(chapterId || ""),
      ].join("|"),
    [],
  );

  // ── save ─────────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!crud.form.chapter_id) return toast.error("Chapter is required");

    if (crud.editing) {
      const entries = topicEntries
        .map((r) => ({
          id: r.id ? String(r.id) : null,
          name: String(r.name || "").trim(),
          order:
            r.order !== "" && !Number.isNaN(Number(r.order))
              ? Number(r.order)
              : undefined,
        }))
        .filter((r) => r.name !== "");
      if (!entries.length) return toast.error("Enter at least one topic name");

      const editingIds = new Set(
        entries.filter((r) => r.id).map((r) => String(r.id)),
      );
      const localKeys = new Set(
        crud.rows
          .filter((r) => !editingIds.has(String(r.id)))
          .map((r) => duplicateTopicKey(r.name, r.chapter_id)),
      );

      setBulkSaving(true);
      let updated = 0;
      let created = 0;
      let skipped = 0;
      try {
        for (const row of entries) {
          const key = duplicateTopicKey(row.name, crud.form.chapter_id);
          if (localKeys.has(key)) {
            skipped++;
            continue;
          }

          if (row.id) {
            await api.put(`/topics/${row.id}`, {
              ...crud.form,
              name: row.name,
              order: row.order,
            });
            updated++;
          } else {
            await api.post("/topics", {
              name: row.name,
              chapter_id: Number(crud.form.chapter_id),
              order: row.order,
              branch_id: Number(branchId),
              session_id: sessionId ? Number(sessionId) : undefined,
            });
            created++;
          }

          localKeys.add(key);
        }

        toast.success(
          `Updated ${updated} topic(s)` +
            (created ? `, created ${created} topic(s)` : "") +
            (skipped ? `, skipped ${skipped} duplicate(s)` : ""),
        );
        crud.setOpen(false);
        crud.load();
        reloadTopics();
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Save failed");
      } finally {
        setBulkSaving(false);
      }
      return;
    }

    if (!validEntries.length)
      return toast.error("Enter at least one topic name");
    setBulkSaving(true);
    let created = 0;
    try {
      for (const row of validEntries) {
        await api.post("/topics", {
          name: row.name,
          chapter_id: Number(crud.form.chapter_id),
          order: row.order,
          branch_id: Number(branchId),
          session_id: sessionId ? Number(sessionId) : undefined,
        });
        created++;
      }
      toast.success(`Created ${created} topic(s)`);
      crud.setOpen(false);
      crud.load();
      reloadTopics();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    } finally {
      setBulkSaving(false);
    }
  };

  const handleDel = async () => {
    await crud.del();
    reloadTopics();
  };

  // when editing a topic, pre-fill class/section/subject/sg from its chapter
  const handleTopicEdit = useCallback(
    (tp) => {
      const ch = chapters.find((c) => String(c.id) === String(tp.chapter_id));
      if (ch) {
        const sg = subjectGroups.find(
          (g) =>
            String(g.class_id) === String(ch.class_id) &&
            (g.subjects || []).some(
              (sid) => String(sid) === String(ch.subject_id),
            ),
        );
        setDClassId(String(ch.class_id || ""));
        setDSectionId(String(ch.section_id || ""));
        setDSgId(sg ? String(sg.id) : "");
        setDSubjectId(String(ch.subject_id || ""));
        loadDialogSections(ch.class_id);
      }

      const sameChapterTopics = crud.rows
        .filter((r) => String(r.chapter_id) === String(tp.chapter_id))
        .sort((a, b) => (a.order ?? 9999) - (b.order ?? 9999));

      setEditTopicEntriesSeed(
        sameChapterTopics.map((r) => ({
          id: r.id,
          name: String(r.name || ""),
          order: r.order === 0 ? "0" : String(r.order || ""),
        })),
      );
      crud.openEdit(tp);
    },
    [chapters, subjectGroups, loadDialogSections, crud],
  );

  const chapterMeta = useCallback(
    (ch) => {
      if (!ch) return "";
      const cls = resolve(classes, ch.class_id);
      const sec = resolve(allSections, ch.section_id);
      const subj = resolve(subjects, ch.subject_id);
      const term = resolve(terms, ch.term_id);
      return [cls, sec ? `Sec ${sec}` : null, subj, term]
        .filter(Boolean)
        .join(" · ");
    },
    [classes, allSections, subjects, terms],
  );

  const selCls =
    "h-8 text-xs rounded border border-slate-300 px-2 bg-white focus:outline-none focus:ring-1 focus:ring-blue-400";

  return (
    <>
      {/* ── header ── */}
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <h1 className="text-xl font-bold tracking-tight font-serif-display erp-text-primary">
          Topics
        </h1>
        <div className="flex gap-2 flex-wrap">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <Input
              value={crud.search}
              onChange={(e) => crud.setSearch(e.target.value)}
              placeholder="Search..."
              className="pl-9 h-9 w-44 md:w-56"
            />
          </div>
          {permissions.canAdd && (
            <Button
              size="sm"
              onClick={() => crud.openCreate()}
              className="h-9 erp-primary"
            >
              <Plus className="w-3.5 h-3.5 mr-1" />
              Add New
            </Button>
          )}
        </div>
      </div>

      {/* ── filter bar ── */}
      <div className="flex flex-wrap gap-2 mb-3 p-2.5 bg-slate-50 rounded-md border border-slate-200 items-center">
        <select
          value={fClassId}
          onChange={(e) => {
            setFClassId(e.target.value);
            setFChapterId("");
            loadFilterSections(e.target.value);
          }}
          className={selCls}
        >
          <option value="">All Classes</option>
          {classes.map((c) => (
            <option key={c.id} value={String(c.id)}>
              {c.name}
            </option>
          ))}
        </select>
        <select
          value={fSectionId}
          onChange={(e) => {
            setFSectionId(e.target.value);
            setFChapterId("");
          }}
          disabled={!fClassId}
          className={`${selCls} disabled:opacity-50`}
        >
          <option value="">All Sections</option>
          {filterSections.map((s) => (
            <option key={s.id} value={String(s.id)}>
              {s.name}
            </option>
          ))}
        </select>
        <select
          value={fSgId}
          onChange={(e) => {
            setFSgId(e.target.value);
            setFSubjectId("");
            setFChapterId("");
          }}
          className={selCls}
        >
          <option value="">All Subject Groups</option>
          {sgsByFilterClass.map((g) => (
            <option key={g.id} value={String(g.id)}>
              {g.name}
            </option>
          ))}
        </select>
        <select
          value={fSubjectId}
          onChange={(e) => {
            setFSubjectId(e.target.value);
            setFChapterId("");
          }}
          className={selCls}
        >
          <option value="">All Subjects</option>
          {subjectsByFilterGroup.map((s) => (
            <option key={s.id} value={String(s.id)}>
              {s.name}
            </option>
          ))}
        </select>
        <select
          value={fChapterId}
          onChange={(e) => setFChapterId(e.target.value)}
          className={selCls}
        >
          <option value="">All Chapters</option>
          {filterChapters.map((c) => (
            <option key={c.id} value={String(c.id)}>
              {c.name}
            </option>
          ))}
        </select>
        {hasFilters && (
          <button
            onClick={clearFilters}
            className="h-8 px-2 text-xs text-slate-500 hover:text-red-600 border border-slate-300 rounded bg-white"
          >
            ✕ Clear
          </button>
        )}
        <span className="text-xs text-slate-400 ml-auto">
          {filteredRows.length} topic{filteredRows.length !== 1 ? "s" : ""}
        </span>
      </div>

      {/* ── grouped list ── */}
      {crud.loading ? (
        <ModernLoader
          compact
          title="Loading Topics"
          subtitle="Fetching records"
        />
      ) : groupedRows.length === 0 ? (
        <div
          className="border rounded-md bg-white shadow-sm py-12 text-center text-slate-400 text-xs"
          style={{ borderColor: "var(--erp-border)" }}
        >
          <div className="font-medium mb-1">No topics found</div>
          <div className="text-[11px] text-slate-300">
            Use "Add New" or adjust your filters
          </div>
        </div>
      ) : (
        <div
          className="border rounded-md overflow-hidden bg-white shadow-sm"
          style={{ borderColor: "var(--erp-border)" }}
        >
          {groupedRows.map((group) => {
            const isOpen = !collapsedGroups.has(group.key);
            const ch = chapters.find(
              (c) => String(c.id) === String(group.chapterId),
            );
            const meta = chapterMeta(ch);
            return (
              <div key={group.key} className="border-b last:border-0">
                <button
                  className="w-full flex items-center gap-2 px-4 py-2.5 bg-slate-50 hover:bg-slate-100 text-left transition-colors"
                  onClick={() => toggleGroup(group.key)}
                >
                  <ChevronDown
                    className={`w-3.5 h-3.5 text-slate-400 transition-transform flex-shrink-0 ${isOpen ? "" : "-rotate-90"}`}
                  />
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 flex-1 min-w-0 text-xs">
                    <span className="font-semibold erp-text-primary">
                      {ch?.name || "Unknown Chapter"}
                    </span>
                    {meta && <span className="text-slate-400">({meta})</span>}
                  </div>
                  <span className="text-[11px] text-slate-400 flex-shrink-0">
                    {group.topics.length} topic
                    {group.topics.length !== 1 ? "s" : ""}
                  </span>
                </button>

                {isOpen && (
                  <div>
                    {group.topics.map((tp) => (
                      <div
                        key={tp.id}
                        className="flex items-center gap-3 px-4 py-2 pl-9 border-t border-slate-100 hover:bg-slate-50/70 transition-colors"
                      >
                        <span className="w-7 text-xs text-slate-400 font-mono text-right flex-shrink-0">
                          {tp.order ?? "—"}
                        </span>
                        <span className="flex-1 text-sm text-slate-800">
                          {tp.name}
                        </span>
                        <div className="flex gap-0.5 flex-shrink-0">
                          {permissions.canEdit && (
                            <button
                              className="p-1.5 rounded hover:bg-blue-50 erp-text-primary"
                              onClick={() => handleTopicEdit(tp)}
                              title="Edit topic"
                            >
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                          )}
                          {permissions.canDelete && (
                            <button
                              className="p-1.5 rounded hover:bg-red-50 text-red-600"
                              onClick={() => crud.setDeleteRow(tp)}
                              title="Delete topic"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── dialog ── */}
      <Dialog open={crud.open} onOpenChange={crud.setOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif-display erp-text-primary">
              {crud.editing ? "Edit" : "Add New"} · Topic
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {/* context selectors to narrow chapter list */}
            <FSel
              label="Class"
              value={dClassId}
              onChange={(v) => {
                setDClassId(v);
                setDSectionId("");
                setDSubjectId("");
                crud.setForm((f) => ({ ...f, chapter_id: "" }));
                loadDialogSections(v);
              }}
              options={classes.map((c) => ({
                value: String(c.id),
                label: c.name,
              }))}
              placeholder="— Filter by Class —"
            />
            <FSel
              label="Section"
              value={dSectionId}
              onChange={(v) => {
                setDSectionId(v);
                crud.setForm((f) => ({ ...f, chapter_id: "" }));
              }}
              options={dSections.map((s) => ({
                value: String(s.id),
                label: s.name,
              }))}
              placeholder={
                dClassId ? "— All Sections —" : "— Select Class first —"
              }
            />
            <FSel
              label="Subject Group"
              value={dSgId}
              onChange={(v) => {
                setDSgId(v);
                setDSubjectId("");
                crud.setForm((f) => ({ ...f, chapter_id: "" }));
              }}
              options={sgsByDialogClass.map((g) => ({
                value: String(g.id),
                label: g.name,
              }))}
              placeholder={
                dClassId ? "— Select Subject Group —" : "— Select Class first —"
              }
            />
            <FSel
              label="Subject"
              value={dSubjectId}
              onChange={(v) => {
                setDSubjectId(v);
                crud.setForm((f) => ({ ...f, chapter_id: "" }));
              }}
              options={subjectsByDialogGroup.map((s) => ({
                value: String(s.id),
                label: s.name,
              }))}
              placeholder="— All Subjects —"
            />

            <div className="md:col-span-2">
              <FSel
                label="Chapter"
                required
                value={crud.form.chapter_id}
                onChange={(v) => crud.setForm((f) => ({ ...f, chapter_id: v }))}
                options={dialogChapters.map((ch) => ({
                  value: String(ch.id),
                  label:
                    ch.name + (chapterMeta(ch) ? ` (${chapterMeta(ch)})` : ""),
                }))}
              />
              {dialogChapters.length === 0 && (dClassId || dSubjectId) && (
                <p className="text-[11px] text-amber-600 mt-1">
                  No chapters found for the selected context. Create chapters
                  first.
                </p>
              )}
            </div>

            <div className="md:col-span-2">
              <div className="flex items-center justify-between mb-2">
                <FLabel required>Topic + Order (multiple)</FLabel>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={addTopicRow}
                  className="h-7 text-xs px-2"
                >
                  <Plus className="w-3 h-3 mr-1" />
                  Add More
                </Button>
              </div>
              <div className="space-y-2">
                {topicEntries.map((entry, idx) => (
                  <div key={idx} className="flex gap-2 items-center">
                    <Input
                      placeholder="Topic Name"
                      value={entry.name}
                      onChange={(e) =>
                        updateTopicRow(idx, "name", e.target.value)
                      }
                      className="h-9 flex-1"
                    />
                    <Input
                      type="number"
                      placeholder="Order"
                      value={entry.order}
                      onChange={(e) =>
                        updateTopicRow(idx, "order", e.target.value)
                      }
                      className="h-9 w-20"
                    />
                    {topicEntries.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeTopicRow(idx)}
                        className="p-1.5 rounded hover:bg-red-50 text-red-500 flex-shrink-0"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-slate-500 mt-1.5">
                {crud.editing
                  ? "Edit opens all topics for this chapter context. Existing rows are updated together; additional rows create new topics."
                  : validEntries.length > 0
                    ? `Will create ${validEntries.length} topic(s) under the selected chapter.`
                    : "Add one or more topics for the selected chapter."}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => crud.setOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={crud.saving || bulkSaving}
              className="erp-primary"
            >
              {crud.saving || bulkSaving
                ? "Saving..."
                : crud.editing
                  ? "Update Topics"
                  : validEntries.length > 1
                    ? `Create ${validEntries.length} Topics`
                    : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DelConfirm
        row={crud.deleteRow}
        onConfirm={handleDel}
        onCancel={() => crud.setDeleteRow(null)}
      />
    </>
  );
}

// ─── Lesson Plans Tab ─────────────────────────────────────────────────────────

function LessonPlansTab() {
  const { classes, subjects, terms, chapters, topics, allSections } = useLp();
  const { branchId, sessionId } = useAuth();
  const crud = useLpCrud("/lesson-plans");
  const permissions = useRouteModulePermissions("lesson_plan");
  const [formSections, setFormSections] = useState([]);
  const [staffRows, setStaffRows] = useState([]);
  const [ttRows, setTtRows] = useState([]);

  const loadSections = useCallback(
    async (classId) => {
      if (!classId) {
        setFormSections([]);
        return;
      }
      try {
        const { data } = await api.get("/sections", {
          params: { class_id: classId, branch_id: branchId, limit: 500 },
        });
        setFormSections(data.items || []);
      } catch {
        setFormSections([]);
      }
    },
    [branchId],
  );

  useEffect(() => {
    if (crud.open && crud.form.class_id) loadSections(crud.form.class_id);
    if (!crud.open) setFormSections([]);
  }, [crud.open, crud.form.class_id, loadSections]);

  useEffect(() => {
    if (!branchId) return;
    api
      .get("/staff", { params: { branch_id: branchId, limit: 2000 } })
      .then((r) => setStaffRows(r.data.items || []))
      .catch(() => setStaffRows([]));

    api
      .get("/timetables", {
        params: { branch_id: branchId, session_id: sessionId, limit: 2000 },
      })
      .then((r) => setTtRows(r.data.items || []))
      .catch(() => setTtRows([]));
  }, [branchId, sessionId]);

  const resolve = (arr, id, field = "name") =>
    arr.find((x) => String(x.id) === String(id))?.[field] ?? null;

  const filteredChapters = useMemo(() => {
    if (!crud.form.class_id && !crud.form.subject_id && !crud.form.term_id)
      return chapters;
    return chapters.filter((ch) => {
      if (
        crud.form.class_id &&
        String(ch.class_id) !== String(crud.form.class_id)
      )
        return false;
      if (
        crud.form.subject_id &&
        String(ch.subject_id) !== String(crud.form.subject_id)
      )
        return false;
      if (crud.form.term_id && String(ch.term_id) !== String(crud.form.term_id))
        return false;
      return true;
    });
  }, [chapters, crud.form.class_id, crud.form.subject_id, crud.form.term_id]);

  const filteredTopics = useMemo(() => {
    if (!crud.form.chapter_id) return [];
    return topics.filter(
      (t) => String(t.chapter_id) === String(crud.form.chapter_id),
    );
  }, [topics, crud.form.chapter_id]);

  const timetableMatches = useMemo(() => {
    if (!crud.form.class_id || !crud.form.section_id || !crud.form.date)
      return [];
    const day = dayNameFromDate(crud.form.date);
    return ttRows
      .filter((r) => String(r.class_id) === String(crud.form.class_id))
      .filter(
        (r) =>
          String(r.section_id || "") === String(crud.form.section_id || ""),
      )
      .filter((r) => {
        const planDate = String(r.plan_date || "").slice(0, 10);
        if (planDate) return planDate === String(crud.form.date);
        return String(r.day_of_week || "") === day;
      })
      .filter((r) =>
        crud.form.subject_id
          ? String(r.subject_id || "") === String(crud.form.subject_id)
          : true,
      )
      .sort((a, b) => Number(a.period_no || 0) - Number(b.period_no || 0));
  }, [
    ttRows,
    crud.form.class_id,
    crud.form.section_id,
    crud.form.date,
    crud.form.subject_id,
  ]);

  useEffect(() => {
    if (!crud.open) return;
    const currentPeriod = Number(crud.form.period_no || 0);
    if (currentPeriod > 0) {
      const nextLetter = periodToLetter(currentPeriod);
      if (String(crud.form.plan_letter || "") !== nextLetter) {
        crud.setForm((f) => ({ ...f, plan_letter: nextLetter }));
      }
      return;
    }
    if (!timetableMatches.length) return;
    const first = timetableMatches[0];
    crud.setForm((f) => ({
      ...f,
      period_no: Number(first.period_no || 0) || "",
      plan_letter: periodToLetter(first.period_no),
      teacher_id: first.teacher_id ? String(first.teacher_id) : f.teacher_id,
    }));
  }, [
    crud.open,
    crud.form.period_no,
    crud.form.plan_letter,
    timetableMatches,
    crud,
  ]);

  const filtered = useMemo(() => {
    const q = crud.search.trim().toLowerCase();
    if (!q) return crud.rows;
    return crud.rows.filter((r) => {
      const hay = [
        r.date,
        resolve(classes, r.class_id),
        resolve(subjects, r.subject_id),
        resolve(terms, r.term_id),
        resolve(chapters, r.chapter_id),
        resolve(staffRows, r.teacher_id, "name"),
        r.plan_letter,
        stripHtml(r.learning_outcomes || ""),
      ]
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [crud.rows, crud.search, classes, subjects, terms, chapters, staffRows]);

  const openPrintA4 = useCallback(
    (row) => {
      const cls = resolve(classes, row.class_id) || "";
      const sec = resolve(allSections, row.section_id) || "";
      const subj = resolve(subjects, row.subject_id) || "";
      const term = resolve(terms, row.term_id) || "";
      const chapter = resolve(chapters, row.chapter_id) || "";
      const topic = resolve(topics, row.topic_id) || "";
      const teacher = resolve(staffRows, row.teacher_id, "name") || "";
      const objective = row.objective || "";
      const learningOutcomes = row.learning_outcomes || "";
      const materials = row.materials || "";
      const activities = row.activities || "";
      const homework = row.homework || "";

      const w = window.open("", "_blank", "width=980,height=900");
      if (!w) {
        toast.error("Popup blocked. Allow popups to print PDF.");
        return;
      }
      w.document.write(`<!doctype html>
<html><head><meta charset="utf-8" /><title>Lesson Plan</title>
<style>
@page { size: A4; margin: 12mm; }
body { font-family: Georgia, 'Times New Roman', serif; color:#1f2937; }
.sheet { border:1px solid #d1d5db; border-radius:8px; padding:10px; }
.title { text-align:center; font-size:28px; letter-spacing:1px; font-weight:700; margin:4px 0 10px; }
.grid { display:grid; grid-template-columns: 1fr 1fr; gap:8px 12px; font-size:13px; }
.line { border-bottom:1px dotted #9ca3af; min-height:18px; }
.block { margin-top:10px; }
.box { border:1px solid #d1d5db; border-radius:6px; padding:8px; min-height:90px; }
.head { font-weight:700; margin-bottom:6px; font-size:13px; }
table { width:100%; border-collapse:collapse; font-size:12px; }
th,td { border:1px solid #d1d5db; padding:6px; vertical-align:top; }
th { background:#f8fafc; font-weight:700; }
</style>
</head><body>
<div class="sheet">
  <div class="title">LESSON PLAN</div>
  <div class="grid">
    <div><strong>Date:</strong> <span class="line">${row.date || ""}</span></div>
    <div><strong>Class & Section:</strong> <span class="line">${cls}${sec ? ` - ${sec}` : ""}</span></div>
    <div><strong>Subject:</strong> <span class="line">${subj}</span></div>
    <div><strong>Term:</strong> <span class="line">${term}</span></div>
    <div><strong>Chapter:</strong> <span class="line">${chapter}</span></div>
    <div><strong>Topic:</strong> <span class="line">${topic}</span></div>
    <div><strong>Period No:</strong> <span class="line">${row.period_no || ""}</span></div>
    <div><strong>Letter:</strong> <span class="line">${row.plan_letter || ""}</span></div>
    <div><strong>Teacher:</strong> <span class="line">${teacher}</span></div>
  </div>
  <div class="block"><div class="head">Objective</div><div class="box">${objective || ""}</div></div>
  <div class="block"><div class="head">Learning Outcomes</div><div class="box">${learningOutcomes || ""}</div></div>
  <div class="block"><div class="head">Materials / Resources</div><div class="box">${materials || ""}</div></div>
  <div class="block"><div class="head">Activities</div><div class="box">${activities || ""}</div></div>
  <div class="block"><div class="head">Homework</div><div class="box">${homework || ""}</div></div>
</div>
<script>window.onload=function(){window.print();};</script>
</body></html>`);
      w.document.close();
    },
    [classes, subjects, terms, chapters, topics, staffRows, allSections],
  );

  // Auto-syncs the matching Class/Teacher E-Log row whenever a lesson plan
  // tied to a specific timetable period is saved — same upsert-by-key logic
  // as ELogsTab's "Generate From Timetable", just scoped to this one period
  // and triggered automatically instead of by a manual button click.
  const syncElogsFromLessonPlan = useCallback(
    async (plan) => {
      const periodNo = plan.period_no;
      if (!periodNo || !plan.section_id || !plan.class_id || !plan.subject_id || !plan.date) {
        return;
      }

      for (const logType of ["class", "teacher"]) {
        if (logType === "teacher" && !plan.teacher_id) continue;
        try {
          const { data } = await api.get("/syllabus-logs", {
            params: {
              branch_id: branchId,
              session_id: sessionId,
              date: plan.date,
              class_id: plan.class_id,
              limit: 500,
            },
          });
          const rows = data.items || [];
          const existing = rows.find(
            (r) =>
              String(r.section_id || "") === String(plan.section_id) &&
              String(r.subject_id || "") === String(plan.subject_id) &&
              String(r.period_no || "") === String(periodNo) &&
              (r.log_type || "class") === logType &&
              (logType !== "teacher" ||
                String(r.teacher_id || "") === String(plan.teacher_id)),
          );

          const payload = {
            log_type: logType,
            date: plan.date,
            class_id: plan.class_id,
            section_id: plan.section_id,
            subject_id: plan.subject_id,
            term_id: plan.term_id || "",
            chapter_id: plan.chapter_id || "",
            topic_id: plan.topic_id || "",
            period_no: periodNo,
            plan_letter: plan.plan_letter || "",
            teacher_id: plan.teacher_id || "",
            branch_id: branchId,
            session_id: sessionId,
          };

          if (existing) {
            // Only sync plan-derived fields — leave status/notes exactly as
            // the teacher already set them on the existing e-log.
            await api.put(`/syllabus-logs/${existing.id}`, payload);
          } else {
            await api.post("/syllabus-logs", { ...payload, status: "pending" });
          }
        } catch {
          // Best-effort — a lesson plan save should never fail because
          // e-log sync failed.
        }
      }
    },
    [branchId, sessionId],
  );

  const handleSave = async () => {
    if (!crud.form.class_id) return toast.error("Class is required");
    if (!crud.form.subject_id) return toast.error("Subject is required");
    let plan_letter = crud.form.plan_letter;
    if (crud.form.period_no && !plan_letter) {
      plan_letter = periodToLetter(crud.form.period_no);
      crud.setForm((f) => ({ ...f, plan_letter }));
    }
    const ok = await crud.save();
    if (ok) {
      await syncElogsFromLessonPlan({ ...crud.form, plan_letter });
    }
  };

  return (
    <>
      <LpTable
        title="Lesson Plans"
        testid="lp-plans"
        search={crud.search}
        setSearch={crud.setSearch}
        loading={crud.loading}
        rows={filtered}
        onAdd={
          permissions.canAdd
            ? () =>
                crud.openCreate({ date: new Date().toISOString().slice(0, 10) })
            : null
        }
        onEdit={permissions.canEdit ? crud.openEdit : null}
        onPrint={openPrintA4}
        onDelete={permissions.canDelete ? crud.setDeleteRow : null}
        columns={[
          { key: "date", label: "Date" },
          {
            key: "class_id",
            label: "Class",
            render: (r) =>
              resolve(classes, r.class_id) || (
                <span className="text-slate-300">—</span>
              ),
          },
          {
            key: "subject_id",
            label: "Subject",
            render: (r) =>
              resolve(subjects, r.subject_id) || (
                <span className="text-slate-300">—</span>
              ),
          },
          {
            key: "term_id",
            label: "Term",
            render: (r) =>
              resolve(terms, r.term_id) || (
                <span className="text-slate-300">—</span>
              ),
          },
          {
            key: "chapter_id",
            label: "Chapter",
            render: (r) =>
              resolve(chapters, r.chapter_id) || (
                <span className="text-slate-300">—</span>
              ),
          },
          {
            key: "period_no",
            label: "Period",
            render: (r) =>
              r.period_no || <span className="text-slate-300">—</span>,
          },
          {
            key: "plan_letter",
            label: "Letter",
            render: (r) =>
              r.plan_letter || <span className="text-slate-300">—</span>,
          },
          {
            key: "teacher_id",
            label: "Teacher",
            render: (r) =>
              resolve(staffRows, r.teacher_id, "name") || (
                <span className="text-slate-300">—</span>
              ),
          },
          {
            key: "objective",
            label: "Objective",
            render: (r) =>
              r.objective ? (
                <span title={stripHtml(r.objective)}>
                  {stripHtml(r.objective).slice(0, 40)}
                  {stripHtml(r.objective).length > 40 ? "…" : ""}
                </span>
              ) : (
                <span className="text-slate-300">—</span>
              ),
          },
          {
            key: "learning_outcomes",
            label: "Learning Outcomes",
            render: (r) =>
              r.learning_outcomes ? (
                <span title={stripHtml(r.learning_outcomes)}>
                  {stripHtml(r.learning_outcomes).slice(0, 40)}
                  {stripHtml(r.learning_outcomes).length > 40 ? "…" : ""}
                </span>
              ) : (
                <span className="text-slate-300">—</span>
              ),
          },
        ]}
      />

      <Dialog open={crud.open} onOpenChange={crud.setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif-display erp-text-primary">
              {crud.editing ? "Edit" : "Add New"} · Lesson Plan
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <FDate
              label="Date"
              value={crud.form.date}
              onChange={(v) => crud.setForm((f) => ({ ...f, date: v }))}
            />
            <FSel
              label="Class"
              required
              value={crud.form.class_id}
              onChange={(v) => {
                crud.setForm((f) => ({
                  ...f,
                  class_id: v,
                  section_id: "",
                  chapter_id: "",
                  topic_id: "",
                  teacher_id: "",
                  period_no: "",
                  plan_letter: "",
                }));
                loadSections(v);
              }}
              options={classes.map((c) => ({
                value: String(c.id),
                label: c.name,
              }))}
            />
            <FSel
              label="Section"
              value={crud.form.section_id}
              onChange={(v) => crud.setForm((f) => ({ ...f, section_id: v }))}
              options={formSections.map((s) => ({
                value: String(s.id),
                label: s.name,
              }))}
              placeholder={
                crud.form.class_id
                  ? "— Select Section —"
                  : "— Select Class First —"
              }
            />
            <FSel
              label="Subject"
              required
              value={crud.form.subject_id}
              onChange={(v) =>
                crud.setForm((f) => ({
                  ...f,
                  subject_id: v,
                  chapter_id: "",
                  topic_id: "",
                  teacher_id: "",
                  period_no: "",
                  plan_letter: "",
                }))
              }
              options={subjects.map((s) => ({
                value: String(s.id),
                label: s.name,
              }))}
            />
            <FSel
              label="Term"
              value={crud.form.term_id}
              onChange={(v) =>
                crud.setForm((f) => ({
                  ...f,
                  term_id: v,
                  chapter_id: "",
                  topic_id: "",
                }))
              }
              options={terms.map((t) => ({
                value: String(t.id),
                label: t.name,
              }))}
            />
            <FSel
              label="Chapter"
              value={crud.form.chapter_id}
              onChange={(v) =>
                crud.setForm((f) => ({ ...f, chapter_id: v, topic_id: "" }))
              }
              options={filteredChapters.map((ch) => ({
                value: String(ch.id),
                label: ch.name,
              }))}
              placeholder="— Select Chapter —"
            />
            <FSel
              label="Topic"
              value={crud.form.topic_id}
              onChange={(v) => crud.setForm((f) => ({ ...f, topic_id: v }))}
              options={filteredTopics.map((t) => ({
                value: String(t.id),
                label: t.name,
              }))}
              placeholder={
                crud.form.chapter_id
                  ? "— Select Topic —"
                  : "— Select Chapter First —"
              }
            />
            <FTxa
              label="Teacher"
              value={crud.form.teacher_id}
              onChange={(v) => crud.setForm((f) => ({ ...f, teacher_id: v }))}
              options={staffRows.map((s) => ({
                value: String(s.id),
                label: s.name || s.staff_id || `#${s.id}`,
              }))}
              placeholder="— Select Teacher —"
            />
            <FSel
              label="Timetable Period"
              value={crud.form.period_no}
              onChange={(v) =>
                crud.setForm((f) => ({
                  ...f,
                  period_no: v,
                  plan_letter: periodToLetter(v),
                }))
              }
              options={timetableMatches.map((t) => ({
                value: String(t.period_no || ""),
                label: `Period ${t.period_no}${t.teacher_id ? ` - ${resolve(staffRows, t.teacher_id, "name") || "Teacher"}` : ""}`,
              }))}
              placeholder="— Select Period —"
            />
            <FTxt
              label="Assigned Letter"
              value={
                crud.form.plan_letter || periodToLetter(crud.form.period_no)
              }
              onChange={(v) => crud.setForm((f) => ({ ...f, plan_letter: v }))}
              placeholder="A / B / C ..."
            />
            <div className="md:col-span-2 text-[11px] text-slate-500 -mt-1">
              Letter is auto-assigned from timetable period number for the
              selected class, section, date and subject.
            </div>
            <RichEditor
              label="Objective"
              value={crud.form.objective}
              onChange={(v) => crud.setForm((f) => ({ ...f, objective: v }))}
            />
            <div>
              <RichEditor
                label="Learning Outcomes"
                value={crud.form.learning_outcomes}
                onChange={(v) =>
                  crud.setForm((f) => ({ ...f, learning_outcomes: v }))
                }
              />
              <div className="text-[11px] text-slate-500 mt-1">
                NCERT/CBSE/SCERT learning outcome codes covered by this
                lesson (e.g. EVS 4.3, NIPUN Bharat FLN codes).
              </div>
            </div>
            <RichEditor
              label="Materials / Resources"
              value={crud.form.materials}
              onChange={(v) => crud.setForm((f) => ({ ...f, materials: v }))}
            />
            <RichEditor
              label="Activities"
              value={crud.form.activities}
              onChange={(v) => crud.setForm((f) => ({ ...f, activities: v }))}
            />
            <RichEditor
              label="Homework"
              value={crud.form.homework}
              onChange={(v) => crud.setForm((f) => ({ ...f, homework: v }))}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => crud.setOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={crud.saving}
              className="erp-primary"
            >
              {crud.saving ? "Saving..." : crud.editing ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DelConfirm
        row={crud.deleteRow}
        onConfirm={crud.del}
        onCancel={() => crud.setDeleteRow(null)}
      />
    </>
  );
}

// ─── Class E-Logs Tab ─────────────────────────────────────────────────────────

const STATUS_OPTS = [
  { value: "completed", label: "Completed" },
  { value: "partial", label: "Partial" },
  { value: "pending", label: "Pending" },
  { value: "skipped", label: "Skipped" },
];

const STATUS_COLORS = {
  completed: "text-green-700 bg-green-50",
  partial: "text-amber-700 bg-amber-50",
  pending: "text-slate-600 bg-slate-100",
  skipped: "text-red-700 bg-red-50",
};

function ELogsTab({
  logType = "class",
  title = "Class E-Logs",
  testId = "lp-elogs",
}) {
  const { classes, subjects, terms, chapters, topics } = useLp();
  const { branchId, sessionId } = useAuth();
  const crud = useLpCrud("/syllabus-logs");
  const permissions = useRouteModulePermissions("lesson_plan");
  const [formSections, setFormSections] = useState([]);
  const [genSections, setGenSections] = useState([]);
  const [staffRows, setStaffRows] = useState([]);
  const [ttRows, setTtRows] = useState([]);
  const [lessonPlanRows, setLessonPlanRows] = useState([]);
  const [genForm, setGenForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    class_id: "",
    section_id: "",
    teacher_id: "",
  });
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    if (!branchId) return;
    api
      .get("/staff", { params: { branch_id: branchId, limit: 2000 } })
      .then((r) => setStaffRows(r.data.items || []))
      .catch(() => setStaffRows([]));

    api
      .get("/timetables", {
        params: { branch_id: branchId, session_id: sessionId, limit: 2000 },
      })
      .then((r) => setTtRows(r.data.items || []))
      .catch(() => setTtRows([]));

    api
      .get("/lesson-plans", {
        params: { branch_id: branchId, session_id: sessionId, limit: 5000 },
      })
      .then((r) => setLessonPlanRows(r.data.items || []))
      .catch(() => setLessonPlanRows([]));
  }, [branchId, sessionId]);

  const loadSections = useCallback(
    async (classId) => {
      if (!classId) {
        setFormSections([]);
        return;
      }
      try {
        const { data } = await api.get("/sections", {
          params: { class_id: classId, branch_id: branchId, limit: 500 },
        });
        setFormSections(data.items || []);
      } catch {
        setFormSections([]);
      }
    },
    [branchId],
  );

  const loadGenSections = useCallback(
    async (classId) => {
      if (!classId) {
        setGenSections([]);
        return;
      }
      try {
        const { data } = await api.get("/sections", {
          params: { class_id: classId, branch_id: branchId, limit: 500 },
        });
        setGenSections(data.items || []);
      } catch {
        setGenSections([]);
      }
    },
    [branchId],
  );

  useEffect(() => {
    if (crud.open && crud.form.class_id) loadSections(crud.form.class_id);
    if (!crud.open) setFormSections([]);
  }, [crud.open, crud.form.class_id, loadSections]);

  useEffect(() => {
    if (genForm.class_id) {
      loadGenSections(genForm.class_id);
      return;
    }
    setGenSections([]);
  }, [genForm.class_id, loadGenSections]);

  const resolve = (arr, id, field = "name") =>
    arr.find((x) => String(x.id) === String(id))?.[field] ?? null;

  const filteredChapters = useMemo(() => {
    if (!crud.form.class_id && !crud.form.subject_id) return chapters;
    return chapters.filter((ch) => {
      if (
        crud.form.class_id &&
        String(ch.class_id) !== String(crud.form.class_id)
      )
        return false;
      if (
        crud.form.subject_id &&
        String(ch.subject_id) !== String(crud.form.subject_id)
      )
        return false;
      return true;
    });
  }, [chapters, crud.form.class_id, crud.form.subject_id]);

  const filteredTopics = useMemo(() => {
    if (!crud.form.chapter_id) return [];
    return topics.filter(
      (t) => String(t.chapter_id) === String(crud.form.chapter_id),
    );
  }, [topics, crud.form.chapter_id]);

  const modeRows = useMemo(() => {
    if (logType === "teacher") {
      return crud.rows.filter((r) => String(r.log_type || "") === "teacher");
    }
    return crud.rows.filter(
      (r) => !r.log_type || String(r.log_type || "class") === "class",
    );
  }, [crud.rows, logType]);

  const timetableMatches = useMemo(() => {
    if (!crud.form.class_id || !crud.form.section_id || !crud.form.date)
      return [];
    const day = dayNameFromDate(crud.form.date);
    return ttRows
      .filter((r) => String(r.class_id) === String(crud.form.class_id))
      .filter(
        (r) =>
          String(r.section_id || "") === String(crud.form.section_id || ""),
      )
      .filter((r) => {
        const planDate = String(r.plan_date || "").slice(0, 10);
        if (planDate) return planDate === String(crud.form.date);
        return String(r.day_of_week || "") === day;
      })
      .filter((r) =>
        crud.form.subject_id
          ? String(r.subject_id || "") === String(crud.form.subject_id)
          : true,
      )
      .sort((a, b) => Number(a.period_no || 0) - Number(b.period_no || 0));
  }, [
    ttRows,
    crud.form.class_id,
    crud.form.section_id,
    crud.form.date,
    crud.form.subject_id,
  ]);

  const timetableForGenerate = useMemo(() => {
    if (!genForm.class_id || !genForm.section_id || !genForm.date) return [];
    const day = dayNameFromDate(genForm.date);
    return ttRows
      .filter((r) => String(r.class_id) === String(genForm.class_id))
      .filter(
        (r) => String(r.section_id || "") === String(genForm.section_id || ""),
      )
      .filter((r) => {
        const planDate = String(r.plan_date || "").slice(0, 10);
        if (planDate) return planDate === String(genForm.date);
        return String(r.day_of_week || "") === day;
      })
      .filter((r) =>
        logType === "teacher" && genForm.teacher_id
          ? String(r.teacher_id || "") === String(genForm.teacher_id)
          : true,
      )
      .sort((a, b) => Number(a.period_no || 0) - Number(b.period_no || 0));
  }, [
    ttRows,
    genForm.class_id,
    genForm.section_id,
    genForm.date,
    genForm.teacher_id,
    logType,
  ]);

  const reportRows = useMemo(() => {
    if (!genForm.date || !genForm.class_id || !genForm.section_id) return [];
    return modeRows
      .filter((r) => String(r.date || "").slice(0, 10) === String(genForm.date))
      .filter((r) => String(r.class_id || "") === String(genForm.class_id))
      .filter((r) => String(r.section_id || "") === String(genForm.section_id))
      .filter((r) =>
        logType === "teacher" && genForm.teacher_id
          ? String(r.teacher_id || "") === String(genForm.teacher_id)
          : true,
      )
      .sort((a, b) => Number(a.period_no || 0) - Number(b.period_no || 0));
  }, [
    modeRows,
    genForm.date,
    genForm.class_id,
    genForm.section_id,
    genForm.teacher_id,
    logType,
  ]);

  useEffect(() => {
    if (!crud.open || !timetableMatches.length) return;
    const currentPeriod = Number(crud.form.period_no || 0);
    if (currentPeriod <= 0) {
      const first = timetableMatches[0];
      crud.setForm((f) => ({
        ...f,
        period_no: Number(first.period_no || 0) || "",
        plan_letter: periodToLetter(first.period_no),
        teacher_id: first.teacher_id ? String(first.teacher_id) : f.teacher_id,
      }));
      return;
    }
    const letter = periodToLetter(currentPeriod);
    if (String(crud.form.plan_letter || "") !== letter) {
      crud.setForm((f) => ({ ...f, plan_letter: letter }));
    }
  }, [
    crud.open,
    crud.form.period_no,
    crud.form.plan_letter,
    timetableMatches,
    crud,
  ]);

  const filtered = useMemo(() => {
    const q = crud.search.trim().toLowerCase();
    if (!q) return modeRows;
    return modeRows.filter((r) => {
      const hay = [
        r.date,
        resolve(classes, r.class_id),
        resolve(subjects, r.subject_id),
        resolve(chapters, r.chapter_id),
        r.status,
        resolve(staffRows, r.teacher_id, "name"),
        r.plan_letter,
      ]
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [modeRows, crud.search, classes, subjects, chapters, staffRows]);

  const handleSave = async () => {
    if (!crud.form.class_id) return toast.error("Class is required");
    if (logType === "teacher" && !crud.form.teacher_id)
      return toast.error("Teacher is required");
    if (!crud.form.log_type) {
      crud.form.log_type = logType;
    }
    if (!crud.form.plan_letter && crud.form.period_no) {
      crud.form.plan_letter = periodToLetter(crud.form.period_no);
    }
    await crud.save();
  };

  const findMatchingPlan = useCallback(
    (slot) => {
      const exact = lessonPlanRows.find(
        (p) =>
          String(p.date || "").slice(0, 10) === String(genForm.date) &&
          String(p.class_id || "") === String(genForm.class_id) &&
          String(p.section_id || "") === String(genForm.section_id) &&
          String(p.subject_id || "") === String(slot.subject_id || "") &&
          Number(p.period_no || 0) === Number(slot.period_no || 0),
      );
      if (exact) return exact;

      return lessonPlanRows.find(
        (p) =>
          String(p.class_id || "") === String(genForm.class_id) &&
          String(p.section_id || "") === String(genForm.section_id) &&
          String(p.subject_id || "") === String(slot.subject_id || "") &&
          Number(p.period_no || 0) === Number(slot.period_no || 0),
      );
    },
    [lessonPlanRows, genForm.date, genForm.class_id, genForm.section_id],
  );

  const generateFromTimetable = useCallback(async () => {
    if (!genForm.date) return toast.error("Date is required");
    if (!genForm.class_id) return toast.error("Class is required");
    if (!genForm.section_id) return toast.error("Section is required");
    if (logType === "teacher" && !genForm.teacher_id)
      return toast.error("Teacher is required for Teacher E-Logs");
    if (!timetableForGenerate.length)
      return toast.error(
        "No timetable slots found for selected date and class section",
      );

    const existing = new Map(
      modeRows
        .filter(
          (r) => String(r.date || "").slice(0, 10) === String(genForm.date),
        )
        .filter((r) => String(r.class_id || "") === String(genForm.class_id))
        .filter(
          (r) => String(r.section_id || "") === String(genForm.section_id),
        )
        .filter((r) =>
          logType === "teacher"
            ? String(r.teacher_id || "") === String(genForm.teacher_id || "")
            : true,
        )
        .map((r) => [
          [
            String(r.subject_id || ""),
            String(r.period_no || ""),
            String(r.teacher_id || ""),
            String(r.log_type || "class"),
          ].join("|"),
          r,
        ]),
    );

    let created = 0;
    let updated = 0;
    setGenerating(true);
    try {
      for (const slot of timetableForGenerate) {
        if (!slot.subject_id) continue;
        if (logType === "teacher" && !slot.teacher_id) continue;

        const plan = findMatchingPlan(slot);
        const payload = {
          log_type: logType,
          date: genForm.date,
          class_id: Number(genForm.class_id),
          section_id: Number(genForm.section_id),
          subject_id: Number(slot.subject_id),
          term_id: plan?.term_id ? Number(plan.term_id) : undefined,
          chapter_id: plan?.chapter_id ? Number(plan.chapter_id) : undefined,
          topic_id: plan?.topic_id ? Number(plan.topic_id) : undefined,
          teacher_id:
            logType === "teacher"
              ? Number(genForm.teacher_id)
              : slot.teacher_id
                ? Number(slot.teacher_id)
                : undefined,
          period_no: Number(slot.period_no || 0) || undefined,
          plan_letter: periodToLetter(slot.period_no),
          status: "pending",
          notes: "",
          branch_id: Number(branchId),
          session_id: sessionId ? Number(sessionId) : undefined,
        };

        const key = [
          String(payload.subject_id || ""),
          String(payload.period_no || ""),
          String(payload.teacher_id || ""),
          String(payload.log_type || "class"),
        ].join("|");
        const found = existing.get(key);

        if (found?.id) {
          await api.put(`/syllabus-logs/${found.id}`, {
            ...payload,
            status: found.status || payload.status,
            notes: found.notes || payload.notes,
          });
          updated++;
        } else {
          await api.post("/syllabus-logs", payload);
          created++;
        }
      }

      toast.success(
        `Generated logs for ${genForm.date}: created ${created}, updated ${updated}`,
      );
      crud.load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to generate E-Logs");
    } finally {
      setGenerating(false);
    }
  }, [
    genForm.date,
    genForm.class_id,
    genForm.section_id,
    genForm.teacher_id,
    logType,
    timetableForGenerate,
    modeRows,
    findMatchingPlan,
    branchId,
    sessionId,
    crud,
  ]);

  const printDateWisePdf = useCallback(() => {
    if (!reportRows.length)
      return toast.error(
        "No rows to print for selected date and class section",
      );

    const cls = resolve(classes, genForm.class_id) || "";
    const sec =
      resolve(
        formSections.length ? formSections : genSections,
        genForm.section_id,
      ) || "";
    const teacher = resolve(staffRows, genForm.teacher_id, "name") || "";

    const w = window.open("", "_blank", "width=1100,height=900");
    if (!w) {
      toast.error("Popup blocked. Allow popups to print PDF.");
      return;
    }

    const rowsHtml = reportRows
      .map((r, idx) => {
        const subj = resolve(subjects, r.subject_id) || "";
        const ch = resolve(chapters, r.chapter_id) || "";
        const tp = resolve(topics, r.topic_id) || "";
        const tch = resolve(staffRows, r.teacher_id, "name") || "";
        return `<tr>
          <td>${idx + 1}</td>
          <td>${r.period_no || ""}</td>
          <td>${r.plan_letter || ""}</td>
          <td>${subj}</td>
          <td>${ch}</td>
          <td>${tp}</td>
          <td>${tch}</td>
          <td>${r.status || "pending"}</td>
          <td>${String(r.notes || "")}</td>
        </tr>`;
      })
      .join("");

    w.document.write(`<!doctype html>
<html><head><meta charset="utf-8" /><title>${title}</title>
<style>
@page { size: A4; margin: 10mm; }
body { font-family: Arial, sans-serif; color:#0f172a; }
h1 { margin: 0 0 8px; font-size: 20px; }
.meta { font-size: 12px; margin-bottom: 10px; display:grid; grid-template-columns: 1fr 1fr; gap: 4px 12px; }
table { width:100%; border-collapse: collapse; font-size: 11px; }
th, td { border:1px solid #cbd5e1; padding: 6px; vertical-align: top; }
th { background:#f1f5f9; }
</style>
</head><body>
  <h1>${title}</h1>
  <div class="meta">
    <div><strong>Date:</strong> ${genForm.date}</div>
    <div><strong>Class:</strong> ${cls}${sec ? ` - ${sec}` : ""}</div>
    <div><strong>Type:</strong> ${logType === "teacher" ? "Teacher E-Log" : "Class E-Log"}</div>
    <div><strong>Teacher:</strong> ${teacher || "All"}</div>
  </div>
  <table>
    <thead>
      <tr>
        <th>#</th><th>Period</th><th>Letter</th><th>Subject</th><th>Chapter</th><th>Topic</th><th>Teacher</th><th>Status</th><th>Notes</th>
      </tr>
    </thead>
    <tbody>${rowsHtml}</tbody>
  </table>
  <script>window.onload=function(){window.print();};</script>
</body></html>`);
    w.document.close();
  }, [
    reportRows,
    classes,
    subjects,
    chapters,
    topics,
    staffRows,
    genForm.date,
    genForm.class_id,
    genForm.section_id,
    genForm.teacher_id,
    title,
    logType,
    formSections,
    genSections,
  ]);

  return (
    <>
      <div className="rounded-md border border-slate-200 bg-slate-50 p-3 mb-3">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
          <FDate
            label="Date"
            value={genForm.date}
            onChange={(v) => setGenForm((f) => ({ ...f, date: v }))}
          />
          <FSel
            label="Class"
            required
            value={genForm.class_id}
            onChange={(v) =>
              setGenForm((f) => ({ ...f, class_id: v, section_id: "" }))
            }
            options={classes.map((c) => ({
              value: String(c.id),
              label: c.name,
            }))}
          />
          <FSel
            label="Section"
            required
            value={genForm.section_id}
            onChange={(v) => setGenForm((f) => ({ ...f, section_id: v }))}
            options={genSections.map((s) => ({
              value: String(s.id),
              label: s.name,
            }))}
            placeholder={
              genForm.class_id ? "— Select Section —" : "— Select Class First —"
            }
          />
          {logType === "teacher" ? (
            <FSel
              label="Teacher"
              required
              value={genForm.teacher_id}
              onChange={(v) => setGenForm((f) => ({ ...f, teacher_id: v }))}
              options={staffRows.map((s) => ({
                value: String(s.id),
                label: s.name || s.staff_id || `#${s.id}`,
              }))}
              placeholder="— Select Teacher —"
            />
          ) : (
            <div />
          )}
          <div className="flex items-end gap-2">
            <Button
              type="button"
              className="h-9 erp-primary"
              onClick={generateFromTimetable}
              disabled={generating}
            >
              {generating ? "Generating..." : "Generate From Timetable"}
            </Button>
            <Button
              type="button"
              variant="outline"
              className="h-9"
              onClick={printDateWisePdf}
              disabled={!reportRows.length}
            >
              <Printer className="w-3.5 h-3.5 mr-1" />
              PDF A4
            </Button>
          </div>
        </div>
        <div className="text-[11px] text-slate-600 mt-2">
          Logs are generated date-wise from timetable slots and auto-linked to
          lesson plans by class, section, subject, date and period.
        </div>
        {timetableForGenerate.length > 0 && (
          <div className="mt-2 border border-slate-200 rounded bg-white overflow-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-slate-700">
                <tr>
                  <th className="px-2 py-1.5 text-left">Period</th>
                  <th className="px-2 py-1.5 text-left">Letter</th>
                  <th className="px-2 py-1.5 text-left">Subject</th>
                  <th className="px-2 py-1.5 text-left">Teacher</th>
                </tr>
              </thead>
              <tbody>
                {timetableForGenerate.map((t, idx) => (
                  <tr
                    key={`${t.id || "slot"}-${idx}`}
                    className="border-t border-slate-100"
                  >
                    <td className="px-2 py-1.5">{t.period_no || "—"}</td>
                    <td className="px-2 py-1.5">
                      {periodToLetter(t.period_no)}
                    </td>
                    <td className="px-2 py-1.5">
                      {resolve(subjects, t.subject_id) || "—"}
                    </td>
                    <td className="px-2 py-1.5">
                      {resolve(staffRows, t.teacher_id, "name") || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <LpTable
        title={title}
        testid={testId}
        search={crud.search}
        setSearch={crud.setSearch}
        loading={crud.loading}
        rows={filtered}
        onAdd={null}
        onEdit={permissions.canEdit ? crud.openEdit : null}
        onDelete={permissions.canDelete ? crud.setDeleteRow : null}
        columns={[
          { key: "date", label: "Date" },
          {
            key: "class_id",
            label: "Class",
            render: (r) =>
              resolve(classes, r.class_id) || (
                <span className="text-slate-300">—</span>
              ),
          },
          {
            key: "subject_id",
            label: "Subject",
            render: (r) =>
              resolve(subjects, r.subject_id) || (
                <span className="text-slate-300">—</span>
              ),
          },
          {
            key: "chapter_id",
            label: "Chapter",
            render: (r) =>
              resolve(chapters, r.chapter_id) || (
                <span className="text-slate-300">—</span>
              ),
          },
          {
            key: "topic_id",
            label: "Topic",
            render: (r) =>
              resolve(topics, r.topic_id) || (
                <span className="text-slate-300">—</span>
              ),
          },
          {
            key: "teacher_id",
            label: "Teacher",
            render: (r) =>
              resolve(staffRows, r.teacher_id, "name") || (
                <span className="text-slate-300">—</span>
              ),
          },
          {
            key: "period_no",
            label: "Period",
            render: (r) =>
              r.period_no || <span className="text-slate-300">—</span>,
          },
          {
            key: "plan_letter",
            label: "Letter",
            render: (r) =>
              r.plan_letter || <span className="text-slate-300">—</span>,
          },
          {
            key: "status",
            label: "Status",
            render: (r) =>
              r.status ? (
                <span
                  className={`px-2 py-0.5 rounded text-xs font-medium ${
                    STATUS_COLORS[r.status] || "text-slate-600 bg-slate-100"
                  }`}
                >
                  {r.status}
                </span>
              ) : (
                <span className="text-slate-300">—</span>
              ),
          },
          {
            key: "notes",
            label: "Notes",
            render: (r) =>
              r.notes ? (
                <span title={r.notes}>
                  {String(r.notes).slice(0, 40)}
                  {r.notes.length > 40 ? "…" : ""}
                </span>
              ) : (
                <span className="text-slate-300">—</span>
              ),
          },
        ]}
      />

      <Dialog open={crud.open} onOpenChange={crud.setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif-display erp-text-primary">
              {crud.editing ? "Edit" : "Add New"} · {title.replace(/s$/, "")}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <FDate
              label="Date"
              value={crud.form.date}
              onChange={(v) => crud.setForm((f) => ({ ...f, date: v }))}
            />
            <FSel
              label="Class"
              required
              value={crud.form.class_id}
              onChange={(v) => {
                crud.setForm((f) => ({
                  ...f,
                  class_id: v,
                  section_id: "",
                  chapter_id: "",
                  topic_id: "",
                }));
                loadSections(v);
              }}
              options={classes.map((c) => ({
                value: String(c.id),
                label: c.name,
              }))}
            />
            <FSel
              label="Section"
              value={crud.form.section_id}
              onChange={(v) => crud.setForm((f) => ({ ...f, section_id: v }))}
              options={formSections.map((s) => ({
                value: String(s.id),
                label: s.name,
              }))}
              placeholder={
                crud.form.class_id
                  ? "— Select Section —"
                  : "— Select Class First —"
              }
            />
            <FSel
              label="Subject"
              value={crud.form.subject_id}
              onChange={(v) =>
                crud.setForm((f) => ({
                  ...f,
                  subject_id: v,
                  chapter_id: "",
                  topic_id: "",
                }))
              }
              options={subjects.map((s) => ({
                value: String(s.id),
                label: s.name,
              }))}
            />
            <FSel
              label="Teacher"
              required={logType === "teacher"}
              value={crud.form.teacher_id}
              onChange={(v) => crud.setForm((f) => ({ ...f, teacher_id: v }))}
              options={staffRows.map((s) => ({
                value: String(s.id),
                label: s.name || s.staff_id || `#${s.id}`,
              }))}
              placeholder="— Select Teacher —"
            />
            <FSel
              label="Term"
              value={crud.form.term_id}
              onChange={(v) => crud.setForm((f) => ({ ...f, term_id: v }))}
              options={terms.map((t) => ({
                value: String(t.id),
                label: t.name,
              }))}
            />
            <FSel
              label="Chapter"
              value={crud.form.chapter_id}
              onChange={(v) =>
                crud.setForm((f) => ({ ...f, chapter_id: v, topic_id: "" }))
              }
              options={filteredChapters.map((ch) => ({
                value: String(ch.id),
                label: ch.name,
              }))}
              placeholder="— Select Chapter —"
            />
            <FSel
              label="Topic"
              value={crud.form.topic_id}
              onChange={(v) => crud.setForm((f) => ({ ...f, topic_id: v }))}
              options={filteredTopics.map((t) => ({
                value: String(t.id),
                label: t.name,
              }))}
              placeholder={
                crud.form.chapter_id
                  ? "— Select Topic —"
                  : "— Select Chapter First —"
              }
            />
            <FSel
              label="Timetable Period"
              value={crud.form.period_no}
              onChange={(v) =>
                crud.setForm((f) => ({
                  ...f,
                  period_no: v,
                  plan_letter: periodToLetter(v),
                }))
              }
              options={timetableMatches.map((t) => ({
                value: String(t.period_no || ""),
                label: `Period ${t.period_no}`,
              }))}
              placeholder="— Select Period —"
            />
            <FTxt
              label="Assigned Letter"
              value={
                crud.form.plan_letter || periodToLetter(crud.form.period_no)
              }
              onChange={(v) => crud.setForm((f) => ({ ...f, plan_letter: v }))}
              placeholder="A / B / C ..."
            />
            <FSel
              label="Status"
              value={crud.form.status}
              onChange={(v) => crud.setForm((f) => ({ ...f, status: v }))}
              options={STATUS_OPTS}
            />
            <FTxa
              label="Notes"
              value={crud.form.notes}
              onChange={(v) => crud.setForm((f) => ({ ...f, notes: v }))}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => crud.setOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={crud.saving}
              className="erp-primary"
            >
              {crud.saving ? "Saving..." : crud.editing ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DelConfirm
        row={crud.deleteRow}
        onConfirm={crud.del}
        onCancel={() => crud.setDeleteRow(null)}
      />
    </>
  );
}

function TeacherELogsTab() {
  return (
    <ELogsTab
      logType="teacher"
      title="Teacher E-Logs"
      testId="lp-teacher-elogs"
    />
  );
}

function SyllabusAnalyticsTab() {
  const { classes, subjects } = useLp();
  const { branchId, sessionId } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fClassId, setFClassId] = useState("");
  const [fSubjectId, setFSubjectId] = useState("");

  useEffect(() => {
    if (!branchId) return;
    setLoading(true);
    api
      .get("/syllabus-logs", {
        params: { branch_id: branchId, session_id: sessionId, limit: 5000 },
      })
      .then((r) => setRows(r.data.items || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [branchId, sessionId]);

  const resolve = (arr, id, field = "name") =>
    arr.find((x) => String(x.id) === String(id))?.[field] ?? null;

  const filtered = useMemo(() => {
    return rows
      .filter((r) => String(r.log_type || "class") === "class")
      .filter((r) => (fClassId ? String(r.class_id) === fClassId : true))
      .filter((r) => (fSubjectId ? String(r.subject_id) === fSubjectId : true));
  }, [rows, fClassId, fSubjectId]);

  const score = useCallback((status) => {
    if (status === "completed") return 1;
    if (status === "partial") return 0.5;
    return 0;
  }, []);

  const kpis = useMemo(() => {
    const total = filtered.length;
    const completed = filtered.filter((r) => r.status === "completed").length;
    const partial = filtered.filter((r) => r.status === "partial").length;
    const weighted = filtered.reduce((sum, r) => sum + score(r.status), 0);
    const percent = total ? Math.round((weighted / total) * 100) : 0;
    return { total, completed, partial, percent };
  }, [filtered, score]);

  const bySubject = useMemo(() => {
    const map = {};
    filtered.forEach((r) => {
      const key = String(r.subject_id || "none");
      if (!map[key])
        map[key] = {
          name: resolve(subjects, r.subject_id) || "Unknown",
          total: 0,
          weighted: 0,
        };
      map[key].total += 1;
      map[key].weighted += score(r.status);
    });
    return Object.values(map)
      .map((x) => ({
        ...x,
        completion: x.total ? Math.round((x.weighted / x.total) * 100) : 0,
      }))
      .sort((a, b) => b.completion - a.completion)
      .slice(0, 12);
  }, [filtered, subjects, score]);

  const byStatus = useMemo(() => {
    const counts = { completed: 0, partial: 0, pending: 0, skipped: 0 };
    filtered.forEach((r) => {
      const key = String(r.status || "pending");
      if (counts[key] == null) counts[key] = 0;
      counts[key] += 1;
    });
    return [
      { name: "Completed", value: counts.completed, color: "#16a34a" },
      { name: "Partial", value: counts.partial, color: "#f59e0b" },
      { name: "Pending", value: counts.pending, color: "#64748b" },
      { name: "Skipped", value: counts.skipped, color: "#dc2626" },
    ];
  }, [filtered]);

  return (
    <div data-testid="lp-syllabus-analytics" className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-xl font-bold tracking-tight font-serif-display erp-text-primary">
          Syllabus Completion Analytics
        </h1>
      </div>

      <div className="flex flex-wrap gap-2 p-2.5 bg-slate-50 rounded-md border border-slate-200">
        <select
          value={fClassId}
          onChange={(e) => setFClassId(e.target.value)}
          className="h-8 text-xs rounded border border-slate-300 px-2 bg-white"
        >
          <option value="">All Classes</option>
          {classes.map((c) => (
            <option key={c.id} value={String(c.id)}>
              {c.name}
            </option>
          ))}
        </select>
        <select
          value={fSubjectId}
          onChange={(e) => setFSubjectId(e.target.value)}
          className="h-8 text-xs rounded border border-slate-300 px-2 bg-white"
        >
          <option value="">All Subjects</option>
          {subjects.map((s) => (
            <option key={s.id} value={String(s.id)}>
              {s.name}
            </option>
          ))}
        </select>
      </div>

      {loading ? (
        <ModernLoader
          compact
          title="Loading Analytics"
          subtitle="Compiling completion metrics"
        />
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="rounded-md border p-3 bg-white">
              <div className="text-xs text-slate-500">Total Logs</div>
              <div className="text-xl font-semibold">{kpis.total}</div>
            </div>
            <div className="rounded-md border p-3 bg-white">
              <div className="text-xs text-slate-500">Completed</div>
              <div className="text-xl font-semibold text-green-700">
                {kpis.completed}
              </div>
            </div>
            <div className="rounded-md border p-3 bg-white">
              <div className="text-xs text-slate-500">Partial</div>
              <div className="text-xl font-semibold text-amber-700">
                {kpis.partial}
              </div>
            </div>
            <div className="rounded-md border p-3 bg-white">
              <div className="text-xs text-slate-500">Completion %</div>
              <div className="text-xl font-semibold text-blue-700">
                {kpis.percent}%
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            <div className="lg:col-span-2 rounded-md border bg-white p-3 h-[320px]">
              <div className="text-sm font-semibold mb-2">
                Completion by Subject
              </div>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={bySubject}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 11 }}
                    interval={0}
                    angle={-20}
                    textAnchor="end"
                    height={70}
                  />
                  <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                  <Tooltip formatter={(v) => `${v}%`} />
                  <Bar
                    dataKey="completion"
                    fill="#2563eb"
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="rounded-md border bg-white p-3 h-[320px]">
              <div className="text-sm font-semibold mb-2">Status Split</div>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={byStatus}
                    dataKey="value"
                    nameKey="name"
                    outerRadius={90}
                    label
                  >
                    {byStatus.map((entry) => (
                      <Cell key={entry.name} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="rounded-md border bg-white p-3 text-xs text-slate-600">
            Weighted formula: completed = 100%, partial = 50%, pending/skipped =
            0%.
          </div>
        </>
      )}
    </div>
  );
}

// ─── LessonPlanPage ───────────────────────────────────────────────────────────

export function LessonPlanPage() {
  const { branchId, sessionId } = useAuth();
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [allSections, setAllSections] = useState([]);
  const [terms, setTerms] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [topics, setTopics] = useState([]);
  const [subjectGroups, setSubjectGroups] = useState([]);

  const reloadTerms = useCallback(async () => {
    if (!branchId) return;
    try {
      const { data } = await api.get("/terms", {
        params: { branch_id: branchId, limit: 500 },
      });
      setTerms(data.items || []);
    } catch {
      /* silent */
    }
  }, [branchId]);

  const reloadChapters = useCallback(async () => {
    if (!branchId) return;
    try {
      const { data } = await api.get("/chapters", {
        params: { branch_id: branchId, session_id: sessionId, limit: 500 },
      });
      setChapters(data.items || []);
    } catch {
      /* silent */
    }
  }, [branchId, sessionId]);

  const reloadTopics = useCallback(async () => {
    if (!branchId) return;
    try {
      const { data } = await api.get("/topics", {
        params: { branch_id: branchId, session_id: sessionId, limit: 1000 },
      });
      setTopics(data.items || []);
    } catch {
      /* silent */
    }
  }, [branchId, sessionId]);

  useEffect(() => {
    if (!branchId) return;
    api
      .get("/classes", { params: { branch_id: branchId, limit: 500 } })
      .then((r) => setClasses(r.data.items || []))
      .catch(() => {});
    api
      .get("/subjects", { params: { branch_id: branchId, limit: 500 } })
      .then((r) => setSubjects(r.data.items || []))
      .catch(() => {});
    api
      .get("/sections", { params: { branch_id: branchId, limit: 500 } })
      .then((r) => setAllSections(r.data.items || []))
      .catch(() => {});
    api
      .get("/subject-groups", { params: { branch_id: branchId, limit: 500 } })
      .then((r) => setSubjectGroups(r.data.items || []))
      .catch(() => {});
    reloadTerms();
    reloadChapters();
    reloadTopics();
  }, [branchId, reloadTerms, reloadChapters, reloadTopics]);

  return (
    <LpCtx.Provider
      value={{
        classes,
        subjects,
        allSections,
        terms,
        chapters,
        topics,
        subjectGroups,
        reloadTerms,
        reloadChapters,
        reloadTopics,
      }}
    >
      <div data-testid="lesson-plan-page">
        <Sub
          prefix="lp"
          items={[
            { to: "/lesson-plan/terms", label: "Terms" },
            { to: "/lesson-plan/chapters", label: "Chapters" },
            { to: "/lesson-plan/topics", label: "Topics" },
            { to: "/lesson-plan/plans", label: "Lesson Plans" },
            { to: "/lesson-plan/logs", label: "Class E-Logs" },
            { to: "/lesson-plan/teacher-logs", label: "Teacher E-Logs" },
            { to: "/lesson-plan/analytics", label: "Syllabus Analytics" },
          ]}
        />
        <Routes>
          <Route
            index
            element={<Navigate to="/lesson-plan/terms" replace />}
          />
          <Route path="terms" element={<TermsTab />} />
          <Route path="chapters" element={<ChaptersTab />} />
          <Route path="topics" element={<TopicsTab />} />
          <Route path="plans" element={<LessonPlansTab />} />
          <Route path="logs" element={<ELogsTab />} />
          <Route path="teacher-logs" element={<TeacherELogsTab />} />
          <Route path="analytics" element={<SyllabusAnalyticsTab />} />
        </Routes>
      </div>
    </LpCtx.Provider>
  );
}

// ─── Submissions (dedicated page: joined names, status filter, mark-complete) ─

function submissionFileUrl(path) {
  if (!path) return "";
  return /^https?:\/\//i.test(path) ? path : `${BACKEND_URL}${path}`;
}

function SubmissionsPage() {
  const permissions = useRouteModulePermissions("assignment", "submissions");
  const { branchId } = useAuth();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleteRow, setDeleteRow] = useState(null);
  const [filterStatus, setFilterStatus] = useState("");
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    const { data } = await api.get("/assignment-submissions", {
      params: {
        branch_id: branchId,
        status: filterStatus || undefined,
        q: q || undefined,
      },
    });
    setRows(data.items || []);
  }, [branchId, filterStatus, q]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      marks: row.marks ?? "",
      remark: row.remark || "",
      status: row.status || "pending",
    });
    setOpen(true);
  };

  const save = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await api.put(`/assignment-submissions/${editing.id}`, {
        marks: form.marks === "" ? null : Number(form.marks),
        remark: form.remark || "",
        status: form.status || "pending",
      });
      toast.success("Updated");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const markComplete = async (row) => {
    try {
      await api.put(`/assignment-submissions/${row.id}`, { status: "reviewed" });
      toast.success("Marked as reviewed");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const del = async () => {
    if (!deleteRow?.id) return;
    await api.delete(`/assignment-submissions/${deleteRow.id}`);
    toast.success("Deleted");
    setDeleteRow(null);
    load();
  };

  return (
    <div data-testid="submissions-page">
      <PageTitle title="Submissions" subtitle="Review and grade student assignment submissions" />

      <div className="flex flex-wrap gap-2 mb-3">
        <select
          className="h-8 rounded-md border border-slate-300 text-xs px-2 bg-white"
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          data-testid="filter-submission-status"
        >
          <option value="">All Status</option>
          <option value="pending">Pending</option>
          <option value="reviewed">Reviewed</option>
        </select>
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Search student / assignment…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="h-8 pl-7 w-56 text-xs"
            data-testid="search-submissions"
          />
        </div>
      </div>

      <DataTable
        testid="submissions-table"
        columns={[
          { key: "student_name", label: "Student" },
          { key: "assignment_title", label: "Assignment" },
          { key: "class_name", label: "Class" },
          {
            key: "submitted_on",
            label: "Submitted On",
            render: (r) => (r.submitted_on || "").slice(0, 16).replace("T", " "),
          },
          {
            key: "status",
            label: "Status",
            render: (r) => (
              <span
                className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${
                  r.status === "reviewed"
                    ? "bg-emerald-50 text-emerald-700"
                    : "bg-amber-50 text-amber-700"
                }`}
              >
                {r.status === "reviewed" ? "Reviewed" : "Pending"}
              </span>
            ),
          },
          { key: "marks", label: "Marks" },
          {
            key: "actions",
            label: "Action",
            render: (r) => (
              <div className="flex items-center gap-1 justify-end">
                {(r.file_url || r.submission_text) && (
                  <a
                    href={r.file_url ? submissionFileUrl(r.file_url) : undefined}
                    target={r.file_url ? "_blank" : undefined}
                    rel="noopener noreferrer"
                    title={r.file_url ? "View submitted file" : r.submission_text}
                    className="p-1.5 rounded hover:bg-blue-50 erp-text-primary cursor-pointer"
                    data-testid={`sub-view-${r.id}`}
                  >
                    <Eye className="w-3.5 h-3.5" />
                  </a>
                )}
                {permissions.canEdit && r.status !== "reviewed" && (
                  <button
                    className="p-1.5 rounded hover:bg-emerald-50 text-emerald-700"
                    onClick={() => markComplete(r)}
                    title="Mark Complete"
                    data-testid={`sub-complete-${r.id}`}
                  >
                    <CheckCircle2 className="w-3.5 h-3.5" />
                  </button>
                )}
                {permissions.canEdit && (
                  <button
                    className="p-1.5 rounded hover:bg-blue-50 erp-text-primary"
                    onClick={() => openEdit(r)}
                    title="Grade / Edit"
                    data-testid={`sub-edit-${r.id}`}
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                )}
                {permissions.canDelete && (
                  <button
                    className="p-1.5 rounded hover:bg-red-50 text-red-600"
                    onClick={() => setDeleteRow(r)}
                    title="Delete"
                    data-testid={`sub-delete-${r.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            ),
          },
        ]}
        rows={rows}
      />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              Grade Submission — {editing?.student_name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-semibold mb-1 block">Marks</Label>
              <Input
                type="number"
                value={form.marks}
                onChange={(e) => setForm((f) => ({ ...f, marks: e.target.value }))}
                className="h-9 text-sm"
                data-testid="sub-marks"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold mb-1 block">Remark</Label>
              <textarea
                className="w-full rounded-md border border-slate-300 text-sm px-2 py-1.5 min-h-[80px]"
                value={form.remark}
                onChange={(e) => setForm((f) => ({ ...f, remark: e.target.value }))}
                data-testid="sub-remark"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold mb-1 block">Status</Label>
              <select
                className="h-9 w-full rounded-md border border-slate-300 text-sm px-2 bg-white"
                value={form.status}
                onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                data-testid="sub-status"
              >
                <option value="pending">Pending</option>
                <option value="reviewed">Reviewed</option>
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button className="erp-primary" onClick={save} disabled={saving} data-testid="sub-save">
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteRow} onOpenChange={(v) => !v && setDeleteRow(null)}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this submission?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove the submission record.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={del} className="bg-rose-600 hover:bg-rose-700 text-white">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ─── AssignmentPage ────────────────────────────────────────────────────────────

const ASSIGNMENT_FIELDS = [
  { key: "title", label: "Title *", required: true },
  {
    key: "term_id",
    label: "Term",
    type: "select",
    optionsEndpoint: "/terms",
    sortOptions: "asc",
    resetOnChange: [
      "class_id",
      "section_id",
      "subject_group_id",
      "subject_id",
      "chapter_id",
      "topic_ids",
    ],
  },
  {
    key: "class_id",
    label: "Class",
    type: "select",
    optionsEndpoint: "/classes",
    resetOnChange: [
      "section_id",
      "subject_group_id",
      "subject_id",
      "chapter_id",
      "topic_ids",
    ],
  },
  {
    key: "section_id",
    label: "Section",
    type: "select",
    optionsEndpoint: "/sections",
    dependsOn: ["class_id"],
    resetOnChange: ["subject_group_id", "subject_id", "chapter_id", "topic_ids"],
  },
  {
    key: "subject_group_id",
    label: "Subject Group",
    type: "select",
    optionsEndpoint: "/subject-groups",
    // Subject groups are scoped by class only (no section_id column) — depending
    // on section_id here used to block the whole Subject/Chapter cascade behind
    // an unrelated field.
    dependsOn: ["class_id"],
    resetOnChange: ["subject_id", "chapter_id", "topic_ids"],
  },
  {
    key: "subject_id",
    label: "Subject",
    type: "select",
    optionsEndpoint: "/subjects",
    dependsOn: ["subject_group_id"],
    allowedIdsFromSelected: {
      field: "subject_group_id",
      sourceKey: "subjects",
    },
    resetOnChange: ["chapter_id", "topic_ids"],
  },
  {
    key: "chapter_id",
    label: "Chapter",
    type: "select",
    optionsEndpoint: "/chapters",
    clientFilterMap: {
      class_id: "class_id",
      section_id: "section_id",
      subject_id: "subject_id",
      term_id: "term_id",
    },
    requireFilters: ["subject_id"],
    showAllIfFilteredEmpty: true,
    resetOnChange: ["topic_ids"],
  },
  {
    key: "topic_ids",
    label: "Topics (Multiple)",
    type: "multiselect",
    optionsEndpoint: "/topics",
    clientFilterMap: {
      chapter_id: "chapter_id",
    },
    requireFilters: ["chapter_id"],
  },
  { key: "due_date", label: "Due Date", type: "date" },
  {
    key: "description",
    label: "Description",
    type: "textarea",
    full: true,
  },
];

// The generic MasterCrudPage list falls back to raw field values when no
// `columns` prop is given — for select-type fields that means the list shows
// stored ids (class_id, section_id, ...) instead of names. Resolve them here
// against a one-time fetch of each lookup, independent of the create/edit
// dialog's own per-field option fetching.
function AssignmentsListPage() {
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [subjectGroups, setSubjectGroups] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [topics, setTopics] = useState([]);
  const [terms, setTerms] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [c, sec, sg, sub, ch, top, t] = await Promise.all([
          api.get("/classes", { params: { limit: 500 } }),
          api.get("/sections", { params: { limit: 500 } }),
          api.get("/subject-groups", { params: { limit: 500 } }),
          api.get("/subjects", { params: { limit: 500 } }),
          api.get("/chapters", { params: { limit: 1000 } }),
          api.get("/topics", { params: { limit: 2000 } }),
          api.get("/terms", { params: { limit: 100 } }),
        ]);
        setClasses(c.data.items || []);
        setSections(sec.data.items || []);
        setSubjectGroups(sg.data.items || []);
        setSubjects(sub.data.items || []);
        setChapters(ch.data.items || []);
        setTopics(top.data.items || []);
        setTerms(t.data.items || []);
      } catch {
        // MasterCrudPage's own dialog still works even if these lookups fail;
        // the list columns just fall back to the raw ids in that case.
      }
    })();
  }, []);

  const resolve = (arr, id, field = "name") =>
    arr.find((x) => String(x.id) === String(id))?.[field] ?? null;

  const resolveTopicNames = (idsRaw) => {
    if (!idsRaw) return null;
    const ids = Array.isArray(idsRaw)
      ? idsRaw
      : String(idsRaw)
          .split(",")
          .map((v) => v.trim())
          .filter(Boolean);
    const names = ids.map((tid) => resolve(topics, tid)).filter(Boolean);
    return names.length ? names.join(", ") : null;
  };

  const dash = <span className="text-slate-300">—</span>;

  return (
    <MasterCrudPage
      title="Assignments"
      endpoint="/assignments"
      testid="assignments"
      fields={ASSIGNMENT_FIELDS}
      columns={[
        { key: "title", label: "Title" },
        {
          key: "term_id",
          label: "Term",
          render: (r) => resolve(terms, r.term_id) || dash,
          csv: (r) => resolve(terms, r.term_id) || "",
        },
        {
          key: "class_id",
          label: "Class",
          render: (r) => resolve(classes, r.class_id) || dash,
          csv: (r) => resolve(classes, r.class_id) || "",
        },
        {
          key: "section_id",
          label: "Section",
          render: (r) => resolve(sections, r.section_id) || dash,
          csv: (r) => resolve(sections, r.section_id) || "",
        },
        {
          key: "subject_group_id",
          label: "Subject Group",
          render: (r) => resolve(subjectGroups, r.subject_group_id) || dash,
          csv: (r) => resolve(subjectGroups, r.subject_group_id) || "",
        },
        {
          key: "subject_id",
          label: "Subject",
          render: (r) => resolve(subjects, r.subject_id) || dash,
          csv: (r) => resolve(subjects, r.subject_id) || "",
        },
        {
          key: "chapter_id",
          label: "Chapter",
          render: (r) => resolve(chapters, r.chapter_id) || dash,
          csv: (r) => resolve(chapters, r.chapter_id) || "",
        },
        {
          key: "topic_ids",
          label: "Topics",
          render: (r) => resolveTopicNames(r.topic_ids) || dash,
          csv: (r) => resolveTopicNames(r.topic_ids) || "",
        },
        { key: "due_date", label: "Due Date" },
        { key: "description", label: "Description" },
      ]}
    />
  );
}

export function AssignmentPage() {
  return (
    <div data-testid="assignment-page">
      <Sub
        prefix="asg"
        items={[
          { to: "/assignment/assignments", label: "Assignments" },
          { to: "/assignment/submissions", label: "Submissions" },
          { to: "/assignment/nb-correction", label: "NB Correction" },
        ]}
      />
      <Routes>
        <Route index element={<AssignmentsListPage />} />
        <Route path="assignments" element={<AssignmentsListPage />} />
        <Route path="submissions" element={<SubmissionsPage />} />
        <Route
          path="nb-correction"
          element={
            <MasterCrudPage
              title="Notebook Correction"
              endpoint="/notebook-corrections"
              testid="nbc"
              fields={[
                { key: "student_id", label: "Student ID" },
                { key: "subject_id", label: "Subject ID" },
                { key: "chapter", label: "Chapter" },
                { key: "date", label: "Date", type: "date" },
                {
                  key: "status",
                  label: "Status",
                  type: "select",
                  options: [
                    { value: "pending", label: "Pending" },
                    { value: "checked", label: "Checked" },
                  ],
                },
                {
                  key: "remarks",
                  label: "Remarks",
                  type: "textarea",
                  full: true,
                },
              ]}
            />
          }
        />
      </Routes>
    </div>
  );
}
